import React, { useState, useEffect, useRef, useMemo, Component } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import toast from 'react-hot-toast';
import PageHeader from '../components/PageHeader';
import { useAgentWorkspaceTabs, getTabIdFromPath } from '../contexts/AgentWorkspaceTabsContext';
import GrammarCheckedTextArea from '../components/GrammarCheckedTextArea';
import { useGrammarChecker } from '../hooks/useGrammarChecker';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Image from '@tiptap/extension-image';
import {
  Modal,
  Select as AntdSelect,
  Input as AntInput,
  Tag,
  Alert,
  Button as AntButton,
  Typography,
  Avatar,
  Divider,
  List,
  Card,
  Space,
  Flex,
  AutoComplete,
  Progress,
  Badge,
  Tooltip,
  Spin,
  Empty,
  Popover,
  Table,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  SendOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ShopOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  MoreOutlined,
  UpOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  DownOutlined,
  AppstoreOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  BellOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  CloseOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  LinkOutlined,
  CodeOutlined,
  FontSizeOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  SmileOutlined,
} from '@ant-design/icons';

dayjs.extend(relativeTime);
const { Option } = AntdSelect;

/** Catches errors in comments list so a single bad comment doesn't crash the whole ticket. */
class CommentsErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError = () => ({ hasError: true });
  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

interface TicketMacroRule {
  field_key: string;
  equals: string;
}

interface TicketMacro {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  apply_to_comment_type: 'both' | 'public' | 'internal';
  auto_apply: boolean;
  auto_apply_category_ids?: string[];
  auto_apply_ticket_types?: string[];
  auto_apply_custom_field_rules?: TicketMacroRule[];
}

// react-mentions doesn't need registration

const AgentWorkspace: React.FC = () => {
  const { id, ticketNumber } = useParams<{ id?: string; ticketNumber?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fromCallMeId = searchParams.get('fromCallMe') || undefined;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Determine if we're using ticket_number or UUID
  const ticketIdentifier = ticketNumber || id;
  const isNewTicket = ticketIdentifier === 'new';
  
  // Check if identifier is a UUID (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
  const isUUID = ticketIdentifier && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ticketIdentifier);
  
  // Fetch ticket data - use ticket_number endpoint if not UUID (normalize so we always get a ticket object)
  const { data: ticketRaw, isLoading: ticketLoading, error: ticketError } = useQuery(
    ['ticket', ticketIdentifier],
    () => {
      if (isUUID) {
        return api.get(`/api/tickets/${ticketIdentifier}`).then((res) => {
          const d = res.data;
          return d?.ticket ?? d ?? null;
        });
      } else {
        return api.get(`/api/tickets/number/${ticketIdentifier}`).then((res) => {
          const d = res.data;
          return d?.ticket ?? d ?? null;
        });
      }
    },
    { enabled: !!ticketIdentifier && !isNewTicket }
  );
  // Normalize ticket: unwrap if needed and ensure we have a plain object with id or ticket_number (avoid crash on refresh)
  const ticket = (() => {
    try {
      const raw = ticketRaw ?? null;
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
      const t = raw as any;
      if (t.id != null || t.ticket_number != null) return t;
      const inner = t.ticket ?? t.data;
      if (inner && typeof inner === 'object' && !Array.isArray(inner) && (inner.id != null || inner.ticket_number != null)) return inner;
      return null;
    } catch {
      return null;
    }
  })();

  // Get ticket ID for API calls (use ticket.id if available, otherwise use identifier if UUID)
  const ticketIdForApi = ticket?.id || (isUUID ? ticketIdentifier : null);
  
  // If it's a ticket_number (not UUID and not 'new'), redirect to ticket_number URL
  useEffect(() => {
    if (ticketIdentifier && !isNewTicket && ticket && ticket.ticket_number) {
      // If we loaded via UUID but have ticket_number, update URL to use ticket_number
      if (isUUID && ticket.ticket_number && ticket.ticket_number !== ticketIdentifier) {
        navigate(`/workspace/number/${ticket.ticket_number}`, { replace: true });
      }
    }
  }, [ticketIdentifier, ticket, isNewTicket, isUUID, navigate]);

  if (ticketLoading && !isNewTicket) {
    return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }} />;
  }

  if (ticketError && !isNewTicket) {
    return (
      <div style={{ padding: 24, backgroundColor: 'white', height: '100%' }}>
        <Typography.Title level={4} type="danger">Failed to load ticket</Typography.Title>
        <Typography.Text type="secondary">{(ticketError as any)?.message || 'Please try again or go back.'}</Typography.Text>
      </div>
    );
  }

  if (!ticket && !isNewTicket) {
    return (
      <div style={{ padding: 24, backgroundColor: 'white', height: '100%' }}>
        <Typography.Title level={4} type="danger">Ticket not found</Typography.Title>
      </div>
    );
  }

  return (
    <AgentWorkspaceContent
      ticket={ticket}
      isNewTicket={isNewTicket}
      ticketIdentifier={ticketIdentifier}
    />
  );
};


interface AgentWorkspaceContentProps {
  ticket: any;
  isNewTicket: boolean;
  ticketIdentifier: string | undefined;
}

const AgentWorkspaceContent: React.FC<AgentWorkspaceContentProps> = ({ ticket, isNewTicket, ticketIdentifier: ticketIdentifierProp }) => {
  const ticketIdentifier = ticketIdentifierProp ?? '';
  const isUUID = ticketIdentifier && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(ticketIdentifier);
  const ticketIdForApi = ticket?.id || (isUUID ? ticketIdentifier : null);
  const navigate = useNavigate();
  const location = useLocation();
  const tabsContext = useAgentWorkspaceTabs();
  const [searchParams] = useSearchParams();
  const fromCallMeId = searchParams.get('fromCallMe') || undefined;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // State management
  const [commentText, setCommentText] = useState('');
  const [commentType, setCommentType] = useState('internal'); // 'public' or 'internal'
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showContextPanel, setShowContextPanel] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [textEditorRef, setTextEditorRef] = useState<HTMLDivElement | null>(null);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionRange, setMentionRange] = useState<{ from: number; to: number } | null>(null);
  const [assistField, setAssistField] = useState('');
  const [commentPlainText, setCommentPlainText] = useState('');
  const [showCommentGrammarIssues, setShowCommentGrammarIssues] = useState(false);
  const [selectedMacroId, setSelectedMacroId] = useState<string | undefined>(undefined);
  const [appliedAutoMacroIds, setAppliedAutoMacroIds] = useState<string[]>([]);

  // Skip onUpdate when we're programmatically setting content (prevents infinite loop: setContent->onUpdate->setCommentText->useEffect->setContent)
  const isSettingContentRef = useRef(false);
  // Defer editor DOM mount to avoid initial render storm (ticket + comments + editor + users all loading)
  const [editorMounted, setEditorMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEditorMounted(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Add/replace tab when viewing a ticket (Zendesk-style tabs)
  useEffect(() => {
    if (!tabsContext || isNewTicket) return;
    // Normalize to ticket-number tab IDs whenever possible so switching tabs
    // does not create UUID-vs-number duplicates that appear to reorder tabs.
    if (isUUID && !ticket?.ticket_number) return;

    const tabId = ticket?.ticket_number
      ? `number/${ticket.ticket_number}`
      : getTabIdFromPath(location.pathname);
    if (!tabId) return;
    const label = ticket?.ticket_number ? `#${ticket.ticket_number}` : (ticket?.subject ? `${(ticket.subject as string).slice(0, 20)}...` : tabId);
    const state = location.state as { openInNewTab?: boolean; replaceTabs?: boolean } | null;
    if (state?.replaceTabs) {
      tabsContext.replaceTabs(tabId, label);
    } else {
      const priority = ticket?.priority as 'low' | 'medium' | 'high' | 'urgent' | undefined;
      tabsContext.addTab(tabId, label, priority);
    }
  }, [tabsContext, isNewTicket, isUUID, location.pathname, location.state, ticket?.ticket_number, ticket?.subject, ticket?.priority]);

  // Ref for React mention dropdown keyboard handling (ArrowUp/Down, Enter) so handler always has latest state
  const mentionDropdownStateRef = useRef<{
    show: boolean;
    index: number;
    getUsers: () => any[];
    setIndex: (arg: number | ((prev: number) => number)) => void;
    select: (user: any) => void;
  }>({ show: false, index: 0, getUsers: () => [], setIndex: () => {}, select: () => {} });
  
  // Close mention dropdown when clicking outside (but not inside the editor or dropdown)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the Tiptap editor or React mention dropdown
      if (target.closest('.tiptap-editor') || 
          target.closest('[data-mention-dropdown]')) {
        return;
      }
      // Only close mention dropdown, don't clear comment text
      setShowMentionDropdown(false);
      setMentionRange(null);
      setShowEmojiPicker(false);
      setShowLinkDialog(false);
    };
    
    if (showMentionDropdown || showEmojiPicker || showLinkDialog) {
      // Use capture phase to catch events early
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [showMentionDropdown, showEmojiPicker, showLinkDialog]);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(true);
  const [escalationDialog, setEscalationDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [escalationComment, setEscalationComment] = useState('');
  const [editableSubject, setEditableSubject] = useState('');
  const [solveDialog, setSolveDialog] = useState(false);
  const [solveComment, setSolveComment] = useState('');
  const [solveCommentType, setSolveCommentType] = useState<'public' | 'internal'>('public');
  const [auditOpen, setAuditOpen] = useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpSubject, setFollowUpSubject] = useState('');
  const [followUpDescription, setFollowUpDescription] = useState('');
  
  // Attachment state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Prevent duplicate ticket submissions
  const isSubmittingTicket = useRef(false);
  
  // Followers state
  const [followers, setFollowers] = useState<any[]>([]);
  const [addFollowerDialog, setAddFollowerDialog] = useState(false);
  const [followerType, setFollowerType] = useState<'user' | 'email'>('email');
  const [followerEmail, setFollowerEmail] = useState('');
  const [followerUserId, setFollowerUserId] = useState('');
  
  // New ticket form state
  const [newTicketForm, setNewTicketForm] = useState({
    business_id: '',
    assigned_to: user?.id || '',
    subject: '',
    description: '',
    priority: 'medium',
    status: 'open',
    category_id: '',
    type: ''
  });

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    business: false,
    subject: false,
    description: false,
    category: false,
    type: false
  });

  // Business search state
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);

  // When creating a ticket from Call Me queue, store queue id to send on create and link
  const [callMeQueueIdToLink, setCallMeQueueIdToLink] = useState<string | null>(null);

  // Recent tickets modal state
  const [selectedTicketForModal, setSelectedTicketForModal] = useState<any>(null);
  const [recentTicketsModalVisible, setRecentTicketsModalVisible] = useState(false);

  // All tickets for this business (popup modal)
  const [businessTicketsModalVisible, setBusinessTicketsModalVisible] = useState(false);

  // Fetch business details (for additional fields like retailzpos_portal)
  const businessIdForTicket = (ticket as any)?.business_id;
  const { data: businessDetail } = useQuery(
    ['business', businessIdForTicket],
    () => api.get(`/api/businesses/${businessIdForTicket}`).then(res => res.data?.business || res.data),
    { enabled: !!businessIdForTicket }
  );

  // Fetch business services (for color-coded tags)
  const { data: businessServices = [] } = useQuery(
    ['business-services', businessIdForTicket],
    async () => {
      const res = await api.get(`/api/businesses/${businessIdForTicket}/services`);
      return res.data?.services || res.data || [];
    },
    { enabled: !!businessIdForTicket && !isNewTicket }
  );

  // Fetch ticket comments
  const { data: commentsData, isLoading: commentsLoading } = useQuery(
    ['ticket-comments', ticketIdForApi],
    () => api.get(`/api/tickets/${ticketIdForApi}/comments`).then(res => res.data),
    { enabled: !!ticketIdForApi && !isNewTicket }
  );
  const comments = Array.isArray(commentsData?.comments) ? commentsData.comments : [];

  // Sync editable subject when ticket loads/changes (only when value actually changes to avoid loop)
  const lastSubjectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNewTicket && ticket) {
      const subj = ticket.subject || '';
      if (lastSubjectRef.current !== subj) {
        lastSubjectRef.current = subj;
        setEditableSubject(subj);
      }
    }
  }, [ticket, isNewTicket]);

  // Sync assist field when ticket loads/changes (only when value actually changes to avoid loop)
  const lastAssistRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNewTicket && ticket) {
      const assist = (ticket as any)?.assist_field || '';
      if (lastAssistRef.current !== assist) {
        lastAssistRef.current = assist;
        setAssistField(assist);
      }
    }
  }, [ticket, isNewTicket]);

  // Clear error-boundary reload flag when ticket is loaded (must run every render for consistent hook order)
  useEffect(() => {
    if (ticket) {
      try {
        sessionStorage.removeItem('agent-workspace-reload-attempted');
      } catch (_) { /* ignore */ }
    }
  }, [ticket]);

  // quill-mention handles mention detection automatically, no need for manual listeners

  // Reset form when creating a new ticket (only when not coming from Call Me prefill)
  useEffect(() => {
    if (isNewTicket && !fromCallMeId) {
      setNewTicketForm({
        business_id: '',
        assigned_to: user?.id || '',
        subject: '',
        description: '',
        priority: 'medium',
        status: 'open',
        category_id: '',
        type: ''
      });
      setSelectedBusiness(null);
      setBusinessSearchTerm('');
      setCallMeQueueIdToLink(null);
    }
  }, [isNewTicket, user?.id, fromCallMeId]);

  // Fetch Call Me prefill when opening New Ticket form from "Convert to ticket"
  const { data: callMePrefill } = useQuery(
    ['call-me-prefill', fromCallMeId],
    () => api.get(`/api/call-me/${fromCallMeId}/prefill`).then((r) => r.data),
    { enabled: isNewTicket && !!fromCallMeId }
  );

  // Apply Call Me prefill to the new ticket form once loaded
  useEffect(() => {
    if (!isNewTicket || !callMePrefill) return;
    setNewTicketForm((prev) => ({
      ...prev,
      business_id: callMePrefill.business?.id || prev.business_id,
      subject: callMePrefill.subject || prev.subject,
      description: callMePrefill.description || prev.description,
      priority: 'medium',
      assigned_to: user?.id || prev.assigned_to
    }));
    if (callMePrefill.business) {
      setSelectedBusiness(callMePrefill.business);
      setBusinessSearchTerm(callMePrefill.business.business_name || '');
    }
    if (callMePrefill.call_me_queue_id) {
      setCallMeQueueIdToLink(callMePrefill.call_me_queue_id);
    }
  }, [isNewTicket, callMePrefill, user?.id]);

  const handleSubjectBlur = () => {
    if (!isNewTicket && ticket && editableSubject !== (ticket.subject || '')) {
      updateTicketMutation.mutate({ subject: editableSubject });
    }
  };

  // Fetch users for assignment (normalize so we always get an array)
  const { data: usersData } = useQuery(
    'assignable-users',
    () => api.get('/api/users/assignable').then((res) => {
      const d = res.data;
      return Array.isArray(d) ? d : (d?.users ?? d?.data ?? []);
    }),
    { staleTime: 5 * 60 * 1000 }
  );
  // CRITICAL: stable reference - new [] every render causes useEditor to recreate -> infinite loop
  const users = React.useMemo(
    () => (Array.isArray(usersData) ? usersData : []),
    [usersData]
  );

  // Check if ticket is locked (solved for 7+ days) - must be defined before editor
  const isTicketLocked = React.useMemo(() => {
    if (!ticket || ticket.status !== 'solved' || !ticket.resolved_at) {
      return false;
    }
    const resolvedDate = new Date(ticket.resolved_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return resolvedDate < sevenDaysAgo;
  }, [ticket]);

  // Setup Tiptap editor with mention support
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        renderHTML({ node }) {
          return [
            'span',
            {
              class: 'mention',
              'data-type': 'mention',
              'data-user-id': node.attrs.id,
              'data-label': node.attrs.label,
              style: 'background-color: #e3f2fd; color: #1976d2; padding: 2px 4px; border-radius: 4px; font-weight: 500;',
            },
            `@${node.attrs.label}`,
          ];
        },
        // No suggestion config - React mention dropdown is the only UI (see showMentionDropdown + handleRichMentionSelect)
      }),
    ],
    // Use empty initial - sync via useEffect only. Passing commentText here causes TipTap to sync on every change, triggering onUpdate -> setCommentText -> re-render -> loop.
    content: '',
    onUpdate: ({ editor }) => {
      if (isSettingContentRef.current) return;
      const htmlContent = editor.getHTML();
      // Skip propagating empty/default content to avoid feedback loop
      const isEmptyLike = !htmlContent || htmlContent === '<p></p>' || htmlContent === '<p></p>\n' || htmlContent.trim() === '<p></p>';
      if (isEmptyLike && !commentText) return;
      setCommentText(htmlContent);
      // Update plain text for grammar checking
      setCommentPlainText(editor.getText());
      
      // Extract mentions and update assist field
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const mentionSpans = tempDiv.querySelectorAll('span[data-type="mention"], span[data-user-id], span.mention');
      const mentionedNames: string[] = [];
      
      mentionSpans.forEach((span) => {
        // Try to get the label from data-label attribute
        let userName = span.getAttribute('data-label');
        
        // If not found, try to extract from text content (remove @)
        if (!userName) {
          const text = span.textContent || '';
          userName = text.startsWith('@') ? text.substring(1) : text;
        }
        
        // If still not found, try to get from users array using the user ID
        if (!userName) {
          const userId = span.getAttribute('data-user-id') || span.getAttribute('data-id');
          if (userId && users) {
            const user = users.find((u: any) => u.id === userId);
            if (user) {
              userName = `${user.first_name} ${user.last_name}`;
            }
          }
        }
        
        if (userName && !mentionedNames.includes(userName)) {
          mentionedNames.push(userName);
        }
      });
      
      // Update assist field with all mentioned names
      if (mentionedNames.length > 0) {
        const currentAssistField = assistField || (ticket as any)?.assist_field || '';
        const currentNames = currentAssistField ? currentAssistField.split(',').map((n: string) => n.trim()) : [];
        
        // Merge mentioned names with existing assist field names
        const allNames = Array.from(new Set([...currentNames, ...mentionedNames]));
        const newAssistField = allNames.filter(Boolean).join(', ');
        
        if (newAssistField !== currentAssistField) {
          setAssistField(newAssistField);
        }
      }

      // Detect @ mention to show user dropdown (so the React dropdown appears when typing @)
      const from = editor.state.selection.from;
      const textBefore = editor.state.doc.textBetween(0, from);
      const mentionMatch = textBefore.match(/@(\w*)$/);
      if (mentionMatch) {
        setMentionQuery(mentionMatch[1]);
        setSelectedMentionIndex(0);
        setShowMentionDropdown(true);
        setMentionRange({ from: from - mentionMatch[0].length, to: from });
      } else {
        setShowMentionDropdown(false);
        setMentionRange(null);
      }
    },
    editable: !isTicketLocked,
    editorProps: {
      attributes: {
        class: commentType === 'internal' 
          ? 'tiptap-editor internal-comment' 
          : 'tiptap-editor public-comment',
        style: 'min-height: 160px; padding: 16px; outline: none;',
      },
      handleKeyDown: (view, event) => {
        const s = mentionDropdownStateRef.current;
        if (!s.show) return false;
        const users = s.getUsers();
        if (!users.length) return false;
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          s.setIndex((i: number) => (i <= 0 ? users.length - 1 : i - 1));
          return true;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          s.setIndex((i: number) => (i >= users.length - 1 ? 0 : i + 1));
          return true;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          const user = users[s.index];
          if (user) s.select(user);
          return true;
        }
        if (event.key === 'Escape') {
          setShowMentionDropdown(false);
          setMentionRange(null);
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        
        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file && file.size <= 25 * 1024 * 1024) { // 25MB limit
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target?.result as string;
              if (base64) {
                // Insert image as base64 using the view's editor
                const { state, dispatch } = view;
                const { schema } = state;
                const imageNode = schema.nodes.image.create({ src: base64 });
                const transaction = state.tr.replaceSelectionWith(imageNode);
                dispatch(transaction);
              }
            };
            reader.readAsDataURL(file);
            return true;
          } else if (file && file.size > 25 * 1024 * 1024) {
            toast.error('Image size must be under 25MB');
            return true;
          }
        }
        return false;
      },
    },
  }, [users, isTicketLocked, commentType]);
  
  // Sync editor when commentText changes externally (e.g. after submit clear, paste)
  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    const isEmpty = !commentText || commentText === '<p></p>' || commentText === '<p></p>\n' || (typeof commentText === 'string' && commentText.trim() === '');
    const currentEmpty = !currentHtml || currentHtml === '<p></p>' || currentHtml === '<p></p>\n' || currentHtml.trim() === '';
    if (isEmpty) {
      if (!currentEmpty) {
        isSettingContentRef.current = true;
        editor.commands.setContent('');
        const t = setTimeout(() => { isSettingContentRef.current = false; }, 50);
        return () => clearTimeout(t);
      }
    } else if (commentText !== currentHtml) {
      isSettingContentRef.current = true;
      editor.commands.setContent(commentText);
      const t = setTimeout(() => { isSettingContentRef.current = false; }, 50);
      return () => clearTimeout(t);
    }
  }, [commentText, editor]);

  // Grammar checking for comment editor
  const { issues: commentGrammarIssues, isChecking: isCheckingCommentGrammar, isInitialized: isCommentGrammarInitialized, applySuggestion: applyCommentSuggestion } = useGrammarChecker(
    commentPlainText,
    { enabled: !!editor && !isNewTicket && commentPlainText.length > 0 }
  );

  // Ensure the current ticket assignee appears in the assignee dropdown even if not in users list
  const assigneeOptions = React.useMemo(() => {
    if (!ticket || !ticket.assigned_to) return users;
    const exists = users.some((u: any) => u.id === ticket.assigned_to);
    if (exists) return users;
    return [
      ...users,
      {
        id: ticket.assigned_to,
        first_name: ticket.assigned_to_name || 'Assigned',
        last_name: ticket.assigned_to_last_name || '',
        agent_level: (ticket as any)?.assigned_to_agent_level || null,
      },
    ];
  }, [users, ticket]);

  // Assignee Select options (value, label, searchable) so filterOption receives them reliably
  const assigneeSelectOptionsNew = React.useMemo(() => {
    if (!users?.length) return [];
    return users.map((user: any) => {
      let roleDisplay = user.role_display_name;
      if (!roleDisplay) {
        const roleName = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ') : 'Agent';
        roleDisplay = user.agent_level ? `${roleName} (Level ${user.agent_level})` : roleName;
      }
      const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const searchable = `${displayName} ${roleDisplay} ${(user.email || '')}`.trim();
      return { value: String(user.id), label: displayName, searchable };
    });
  }, [users]);

  const assigneeSelectOptionsTicket = React.useMemo(() => {
    if (!assigneeOptions?.length) return [];
    return assigneeOptions.map((user: any) => {
      let roleDisplay = user.role_display_name;
      if (!roleDisplay) {
        const roleName = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ') : 'Agent';
        roleDisplay = user.agent_level ? `${roleName} (Level ${user.agent_level})` : roleName;
      }
      const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const searchable = `${displayName} ${roleDisplay} ${(user.email || '')}`.trim();
      return { value: String(user.id), label: displayName, searchable };
    });
  }, [assigneeOptions]);

  // Look up user by value for optionRender (Ant Design may not pass custom option props)
  const getUserForOption = (value: string, fromUsers: any[]) => {
    if (!value || !Array.isArray(fromUsers)) return null;
    return fromUsers.find((u: any) => String(u.id) === String(value)) || null;
  };

  // Fetch businesses for new ticket - use search with empty string to get all (capped server-side to 200)
  const { data: businessesData } = useQuery(
    'businesses',
    () => api.get('/api/businesses/search?search=&limit=200').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  );
  const businesses = businessesData?.businesses || [];

  // Fetch last 3 tickets for selected business
  const selectedBusinessId = (selectedBusiness as any)?.id;
  const { data: recentTicketsData, isLoading: recentTicketsLoading } = useQuery(
    ['recent-tickets', selectedBusinessId],
    () => api.get(`/api/tickets?business_id=${selectedBusinessId}&limit=3&page=1`).then(res => res.data),
    { 
      enabled: !!selectedBusinessId && isNewTicket,
      select: (data) => data?.tickets || []
    }
  );

  // Fetch last comment for selected ticket in modal
  const { data: lastCommentData } = useQuery(
    ['ticket-last-comment', selectedTicketForModal?.id],
    () => api.get(`/api/tickets/${selectedTicketForModal?.id}/comments`).then(res => {
      const comments = res.data?.comments || [];
      return comments.length > 0 ? comments[comments.length - 1] : null;
    }),
    { enabled: !!selectedTicketForModal?.id && recentTicketsModalVisible }
  );

  // Fetch business equipment
  const { data: businessEquipment = [] } = useQuery(
    ['businessEquipment', ticket?.business_id],
    () => api.get(`/api/businesses/${ticket?.business_id}/equipment`).then(res => res.data.equipment),
    { enabled: !!ticket?.business_id && !isNewTicket }
  );

  // Ticket history for this business (other tickets, when viewing existing ticket)
  const { data: businessTicketsData, isLoading: businessTicketsLoading } = useQuery(
    ['business-ticket-history', ticket?.business_id, ticket?.id],
    () => api.get(`/api/tickets?business_id=${ticket?.business_id}&limit=20&page=1`).then(res => res.data),
    {
      enabled: !!ticket?.business_id && !!ticket?.id && !isNewTicket,
      select: (data) => {
        const list = data?.tickets || [];
        const currentId = (ticket as any)?.id;
        return currentId ? list.filter((t: any) => t.id !== currentId) : list;
      },
    }
  );
  const businessTicketHistory = businessTicketsData || [];

  // All tickets for business when popup is open (for modal list)
  const { data: businessTicketsAllData, isLoading: businessTicketsAllLoading } = useQuery(
    ['business-tickets-all', ticket?.business_id, businessTicketsModalVisible],
    () => api.get(`/api/tickets?business_id=${ticket?.business_id}&limit=100&page=1`).then(res => res.data),
    { enabled: !!ticket?.business_id && businessTicketsModalVisible }
  );
  const businessTicketsAllList = businessTicketsAllData?.tickets || [];

  // Search businesses (server caps at 200)
  const { data: searchResults = [], isLoading: isSearching } = useQuery(
    ['businesses', 'search', businessSearchTerm],
    () => api.get(`/api/businesses/search?search=${encodeURIComponent(businessSearchTerm)}&limit=200`).then(res => res.data.businesses),
    {
      enabled: isNewTicket && businessSearchTerm.length >= 1,
      staleTime: 30000,
    }
  );

  // Fetch available agents for escalation
  const { data: availableAgentsData } = useQuery(
    ['available-agents', user?.id],
    () => api.get('/api/tickets/agents/available').then(res => res.data),
    { enabled: !!user && (user.role === 'agent' || user.role === 'admin'), staleTime: 5 * 60 * 1000 }
  );
  const availableAgents = availableAgentsData?.agents || [];

  // Categories (cache 5 min to reduce load spikes)
  const { data: categoriesData } = useQuery(
    'categories',
    async () => {
      const res = await api.get('/api/categories');
      return Array.isArray(res.data?.categories) ? res.data.categories : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );
  const categories = categoriesData || [];

  // Ticket Types (cache 5 min)
  const { data: ticketTypesData } = useQuery(
    'ticket-types',
    async () => {
      const res = await api.get('/api/ticket-types');
      return Array.isArray(res.data?.types) ? res.data.types : [];
    },
    { staleTime: 5 * 60 * 1000 }
  );
  const ticketTypes = ticketTypesData || [];

  const { data: macrosData } = useQuery(
    'ticket-macros',
    async () => {
      const res = await api.get('/api/macros');
      return Array.isArray(res.data?.macros) ? (res.data.macros as TicketMacro[]) : [];
    },
    { staleTime: 60 * 1000, enabled: !isNewTicket }
  );
  const ticketMacros = macrosData || [];

  const ticketCategoryId = (ticket as any)?.category_id || null;

  // Custom fields for this ticket
  const { data: customFieldsResp } = useQuery(
    ['ticket-custom-fields', ticketIdForApi, ticketCategoryId],
    async () => {
      if (!ticketIdForApi || isNewTicket) return { fields: [] } as any;
      const res = await api.get(`/api/tickets/${ticketIdForApi}/custom-fields`);
      return res.data;
    },
    { enabled: !!ticketIdForApi && !isNewTicket }
  );
  const customFields = (customFieldsResp as any)?.fields || [];
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const customFieldsRespRef = useRef<typeof customFieldsResp>(undefined);
  useEffect(() => {
    if (customFieldsResp === undefined) return;
    if (customFieldsRespRef.current === customFieldsResp) return; // Avoid setState loop
    customFieldsRespRef.current = customFieldsResp;
    const start: Record<string, any> = {};
    for (const f of (customFieldsResp as any)?.fields || []) start[f.id] = f.value ?? null;
    setCustomValues(start);
  }, [customFieldsResp]);

  // Save a single custom field immediately (used for select/checkbox changes)
  const saveCustomFieldImmediate = async (fieldId: string, value: any) => {
    if (!ticketIdForApi || isNewTicket) return;
    try {
      await api.put(`/api/tickets/${ticketIdForApi}/custom-fields`, { values: [{ field_id: fieldId, value }] });
      queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
      toast.success('Custom field saved');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to save custom field');
    }
  };

  const normalizeMacroValue = (v: any) => String(v ?? '').trim().toLowerCase();

  const macroMatchesCommentType = (macro: TicketMacro) =>
    macro.apply_to_comment_type === 'both' || macro.apply_to_comment_type === commentType;

  const getCustomFieldValueByKey = (fieldKey: string) => {
    const field = customFields.find((f: any) => f.field_key === fieldKey);
    if (!field) return undefined;
    return customValues[field.id];
  };

  const getMatchingAutoMacros = () => {
    const categoryId = (ticket as any)?.category_id || '';
    const ticketType = (ticket as any)?.type || '';
    return (ticketMacros || []).filter((macro: TicketMacro) => {
      if (!macro || !macro.is_active || !macro.auto_apply || !macroMatchesCommentType(macro)) return false;
      const categories = Array.isArray(macro.auto_apply_category_ids) ? macro.auto_apply_category_ids : [];
      const types = Array.isArray(macro.auto_apply_ticket_types) ? macro.auto_apply_ticket_types : [];
      const rules = Array.isArray(macro.auto_apply_custom_field_rules) ? macro.auto_apply_custom_field_rules : [];

      const hasAnyCondition = categories.length > 0 || types.length > 0 || rules.length > 0;
      if (!hasAnyCondition) return false;

      if (categories.length > 0 && !categories.includes(String(categoryId))) return false;
      if (types.length > 0 && !types.includes(String(ticketType))) return false;

      for (const rule of rules) {
        if (!rule?.field_key) continue;
        const actual = normalizeMacroValue(getCustomFieldValueByKey(rule.field_key));
        const expected = normalizeMacroValue(rule.equals);
        if (actual !== expected) return false;
      }
      return true;
    });
  };

  const insertMacroContent = (content: string) => {
    const trimmed = String(content || '').trim();
    if (!trimmed || isTicketLocked) return;
    if (editor) {
      editor.chain().focus().insertContent(`${trimmed}\n`).run();
      return;
    }
    setCommentText((prev) => (prev ? `${prev}\n\n${trimmed}` : trimmed));
  };

  // Update ticket mutation (status, priority, assignee)

  // Create follow-up ticket mutation
  const createFollowUpMutation = useMutation(
    (data: { subject?: string; description?: string }) => 
      api.post(`/api/tickets/${ticketIdForApi}/follow-up`, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['ticket', ticketIdentifier]);
        queryClient.invalidateQueries(['ticket-comments', ticketIdForApi]);
        toast.success(`Follow-up ticket #${response.data.ticket.ticket_number} created successfully`);
        setFollowUpDialogOpen(false);
        setFollowUpSubject('');
        setFollowUpDescription('');
        // Navigate to the new follow-up ticket
        navigate(`/workspace/${response.data.ticket.id}`);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Failed to create follow-up ticket');
      }
    }
  );

  const handleCreateFollowUp = () => {
    if (!followUpSubject.trim() || !followUpDescription.trim()) {
      toast.error('Please fill in both Subject and Additional Description fields');
      return;
    }
    createFollowUpMutation.mutate({
      subject: followUpSubject.trim(),
      description: followUpDescription.trim()
    });
  };

  const updateTicketMutation = useMutation(
    (data: any) => {
      if (!ticketIdForApi) throw new Error('Ticket ID not available');
      return api.put(`/api/tickets/${ticketIdForApi}`, data);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['ticket', ticketIdentifier]);
        queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
        // If category changed, refetch custom fields
        if (variables.category_id !== undefined) {
          queryClient.invalidateQueries(['ticket-custom-fields', ticketIdForApi]);
        }
        toast.success('Ticket updated');
      },
      onError: (error: any) => {
        // Check if error is about custom fields
        if (error.response?.data?.code === 'CUSTOM_FIELDS_REQUIRED') {
          toast.error(error.response?.data?.error || 'Select Problem Type');
        } else {
          toast.error(error.response?.data?.error || 'Failed to update ticket');
        }
      },
    }
  );

  // Create new ticket mutation
  const createTicketMutation = useMutation(
    async (data: any) => {
      
      // Create the ticket first
      const ticketResponse = await api.post('/api/tickets', data);
      
      // Add the description as the initial comment
      if (data.description && ticketResponse.data.id) {
        await api.post(`/api/tickets/${ticketResponse.data.id}/comments`, {
          comment: data.description,
          is_internal: false
        });
      }
      
      return ticketResponse;
    },
    {
      onSuccess: (response, variables: any) => {
        isSubmittingTicket.current = false;
        queryClient.invalidateQueries(['tickets']);
        queryClient.invalidateQueries(['comments', response.data.id]);
        if (variables?.call_me_queue_id) {
          queryClient.invalidateQueries('call-me-count');
          queryClient.invalidateQueries('call-me-queue');
        }
        toast.success('Ticket created successfully');
        navigate(`/workspace/number/${response.data.ticket_number}`);
      },
      onError: (error: any) => {
        isSubmittingTicket.current = false;
        toast.error(error.response?.data?.error || 'Failed to create ticket');
      },
    }
  );

  // Add comment mutation
  const addCommentMutation = useMutation(
    (data: { comment: string; is_internal: boolean; attachment_ids?: string[]; mentioned_user_ids?: string[] }) =>
      api.post(`/api/tickets/${ticketIdForApi}/comments`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket-comments', ticketIdForApi]);
        queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
        setCommentText('');
        setSelectedFiles([]);
        setUploadedAttachments([]);
        // Don't clear assist field - users should remain on the ticket
        
        // Clear the editor content
        if (editor) {
          editor.commands.clearContent();
        }
        
        toast.success('Comment added successfully');
      },
      onError: () => {
        toast.error('Failed to add comment');
      },
    }
  );

  // Audit logs (admin only)
  const { data: auditLogs = [], isLoading: auditLoading } = useQuery(
    ['ticket-audit', ticketIdForApi],
    async () => {
      if (!ticketIdForApi || isNewTicket || !user || user.role !== 'admin') return [];
      const res = await api.get(`/api/tickets/${ticketIdForApi}/audit`);
      return res.data?.logs || [];
    },
    { enabled: !!ticketIdForApi && !isNewTicket && !!user && user.role === 'admin' }
  );

  // Resolve audit value to human-readable label
  const resolveAuditValue = (field: string, value: any): string => {
    if (value === null || value === undefined || value === '') return 'None';
    
    // Handle custom fields - value is a JSON array string
    if (field === 'custom_fields' || field?.includes('custom')) {
      try {
        // Try to parse as JSON if it's a string
        let parsedValue = value;
        if (typeof value === 'string') {
          parsedValue = JSON.parse(value);
        }
        
        // If it's an array of custom field objects
        if (Array.isArray(parsedValue) && parsedValue.length > 0) {
          return parsedValue.map((item: any) => {
            if (item.field_id && item.value !== undefined) {
              const customField = customFields.find((f: any) => f.id === item.field_id);
              const fieldName = customField ? customField.label || customField.name : item.field_id;
              return `${fieldName}: ${item.value}`;
            }
            return JSON.stringify(item);
          }).join(', ');
        }
      } catch (e) {
        // If parsing fails, return as string
      }
    }
    
    const valStr = String(value);
    if (field === 'assigned_to') {
      const u = (users as any[])?.find?.((x: any) => x.id === valStr);
      return u ? `${u.first_name} ${u.last_name}` : (valStr === 'null' ? 'Unassigned' : valStr);
    }
    if (field === 'category_id') {
      const c = (categories as any[])?.find?.((x: any) => x.id === valStr);
      return c ? c.name : (valStr === 'null' ? 'None' : valStr);
    }
    if (field === 'follower_added' || field === 'follower_removed') {
      return valStr;
    }
    return valStr;
  };

  // Resolve audit field name to human-readable label
  const resolveAuditFieldName = (field: string, value: any): string => {
    // Handle custom fields
    if (field === 'custom_fields' || field?.includes('custom')) {
      try {
        let parsedValue = value;
        if (typeof value === 'string') {
          parsedValue = JSON.parse(value);
        }
        
        if (Array.isArray(parsedValue) && parsedValue.length > 0) {
          const fieldNames = parsedValue.map((item: any) => {
            if (item.field_id) {
              const customField = customFields.find((f: any) => f.id === item.field_id);
              return customField ? (customField.label || customField.name) : item.field_id;
            }
            return null;
          }).filter(Boolean);
          
          if (fieldNames.length > 0) {
            return fieldNames.join(', ');
          }
        }
      } catch (e) {
        // If parsing fails, return original field name
      }
    }
    
    // Map other field names to readable labels
    const fieldMap: Record<string, string> = {
      'assigned_to': 'Assignee',
      'category_id': 'Category',
      'status': 'Status',
      'priority': 'Priority',
      'type': 'Type',
      'subject': 'Subject',
      'follower_added': 'Follower Added',
      'follower_removed': 'Follower Removed',
    };
    
    return fieldMap[field] || field;
  };

  // Refetch audit logs when modal opens to avoid stale data
  useEffect(() => {
    if (auditOpen && ticketIdForApi) {
      queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
    }
  }, [auditOpen, ticketIdForApi, queryClient]);

  // Escalation mutation
  const escalationMutation = useMutation(
    (data: { assigned_to: string; comment?: string }) =>
      api.post(`/api/tickets/${ticketIdForApi}/escalate`, data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['ticket', ticketIdentifier]);
        queryClient.invalidateQueries(['ticket-comments', ticketIdForApi]);
        queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
        setEscalationDialog(false);
        setSelectedAgent('');
        setEscalationComment('');
        toast.success(`Ticket escalated to ${response.data.escalated_to.name}`);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to escalate ticket');
      },
    }
  );

  // Load followers when ticket changes
  useEffect(() => {
    const loadFollowers = async () => {
      if (!ticket?.id || isNewTicket) {
        setFollowers([]);
        return;
      }

      try {
        const response = await fetch(`/api/tickets/${ticket.id}/followers`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setFollowers(data.followers || []);
        }
      } catch (error) {
        console.error('Failed to load followers:', error);
      }
    };

    loadFollowers();
  }, [ticket?.id, isNewTicket]);

  // File upload functions
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 25 * 1024 * 1024; // 25MB limit
      return isValidType && isValidSize;
    });
    
    if (validFiles.length !== files.length) {
      toast.error('Only images and PDFs under 25MB are allowed');
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (): Promise<string[]> => {
    if (selectedFiles.length === 0) return [];
    
    setUploadingFiles(true);
    const uploadedIds: string[] = [];
    
    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('attachment', file);
        formData.append('is_internal', commentType === 'internal' ? 'true' : 'false');
        
        const response = await api.post(`/api/tickets/${ticketIdForApi}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedIds.push(response.data.attachment.id);
      }
      
      setUploadedAttachments(prev => [...prev, ...uploadedIds]);
      setSelectedFiles([]);
      return uploadedIds;
    } catch (error) {
      toast.error('Failed to upload files');
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  // Handle mention functionality
  const handleCommentTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setCommentText(value);
    setCursorPosition(cursorPos);

    // Check for @ mentions
    const textBeforeCursor = value.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentionDropdown(true);
      // Position dropdown below the text field
      setMentionPosition({ top: 100, left: 0 });
    } else {
      setShowMentionDropdown(false);
    }
  };

  const handleMentionSelect = (user: any) => {
    const textBeforeCursor = commentText.substring(0, cursorPosition);
    const textAfterCursor = commentText.substring(cursorPosition);
    const beforeMention = textBeforeCursor.replace(/@\w*$/, '');
    
    const newText = `${beforeMention}@${user.first_name} ${user.last_name} ${textAfterCursor}`;
    setCommentText(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');
  };

  const getFilteredUsers = () => {
    if (!users || users.length === 0) return [];
    if (!mentionQuery || mentionQuery.trim() === '') return users || [];
    const query = mentionQuery.toLowerCase().trim();
    const filtered = (users || []).filter((user: any) => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().trim();
      const firstName = (user.first_name || '').toLowerCase();
      const lastName = (user.last_name || '').toLowerCase();
      return fullName.includes(query) || firstName.startsWith(query) || lastName.startsWith(query);
    });
    return filtered;
  };

  const extractMentions = (htmlContent: string) => {
    const mentions: string[] = [];
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Find all mention spans - Tiptap stores them as span[data-type="mention"] with data-user-id
    const mentionSpans = tempDiv.querySelectorAll('span[data-type="mention"], span[data-user-id], span.mention');
    mentionSpans.forEach((span) => {
      // Try data-user-id first (our custom attribute)
      let userId = span.getAttribute('data-user-id');
      
      // If not found, try data-id (Tiptap's default)
      if (!userId) {
        userId = span.getAttribute('data-id');
      }
      
      // If still not found, try to extract from the mention node's data attributes
      if (!userId && (span as any).dataset) {
        userId = (span as any).dataset.id || (span as any).dataset.userId;
      }
      
      if (userId && !mentions.includes(userId)) {
        mentions.push(userId);
      }
    });
    
    return mentions;
  };

  // Rich text editor functions
  const formatText = (command: string, value?: string) => {
    if (textEditorRef) {
      textEditorRef.focus();
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        if (command === 'insertHTML' && value) {
          // Use modern approach for inserting HTML
          range.deleteContents();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = value;
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          range.insertNode(fragment);
          range.collapse(false);
        } else {
          // Fallback to execCommand for basic formatting
          document.execCommand(command, false, value);
        }
        
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const insertEmoji = (emoji: string) => {
    if (editor) {
      editor.chain().focus().insertContent(emoji).run();
    }
    setShowEmojiPicker(false);
  };

  const insertLink = () => {
    console.log('insertLink called with:', { linkUrl, linkText });
    if (linkUrl && linkText) {
      const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      console.log('Inserting link HTML:', linkHtml);
      formatText('insertHTML', linkHtml);
    } else {
      console.log('Missing linkUrl or linkText');
    }
    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
  };


  const insertStrikeThrough = () => {
    formatText('strikeThrough');
  };

  const insertCode = () => {
    formatText('insertHTML', '<code>code</code>');
  };

  const insertQuote = () => {
    formatText('insertHTML', '<blockquote>quote</blockquote>');
  };

  const handleRichTextChange = () => {
    if (textEditorRef) {
      const content = textEditorRef.innerHTML;
      setCommentText(content);
      
      // Check for @ mentions in the content
      const textContent = textEditorRef.textContent || '';
      const cursorPos = getCaretPosition();
      const textBeforeCursor = textContent.substring(0, cursorPos);
      const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
      
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentionDropdown(true);
      setSelectedMentionIndex(0); // Reset selection when dropdown opens
      setMentionPosition({ top: 100, left: 0 });
    } else {
      setShowMentionDropdown(false);
    }
    }
  };

  const getCaretPosition = () => {
    if (textEditorRef) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(textEditorRef);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        return preCaretRange.toString().length;
      }
    }
    return 0;
  };

  // Insert mention via Tiptap editor when user is selected from the React dropdown

  const handleRichMentionSelect = (user: any) => {
    const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    if (editor && mentionRange) {
      editor
        .chain()
        .focus()
        .deleteRange(mentionRange)
        .insertContent([{ type: 'mention', attrs: { id: user.id, label: userName } }])
        .insertContent(' ')
        .run();
      // Update assist field
      const currentAssistField = assistField || (ticket as any)?.assist_field || '';
      if (currentAssistField) {
        if (!currentAssistField.includes(userName)) {
          setAssistField(currentAssistField + ', ' + userName);
        }
      } else {
        setAssistField(userName);
      }
    }
    setShowMentionDropdown(false);
    setMentionQuery('');
    setMentionRange(null);
  };

  // Keep ref updated so editor handleKeyDown can drive React mention dropdown (no Tiptap suggestion)
  useEffect(() => {
    mentionDropdownStateRef.current = {
      show: showMentionDropdown,
      index: selectedMentionIndex,
      getUsers: getFilteredUsers,
      setIndex: setSelectedMentionIndex,
      select: handleRichMentionSelect,
    };
  }, [showMentionDropdown, selectedMentionIndex, mentionQuery]);

  const handleRemoveFromAssist = (nameToRemove: string) => {
    const currentAssistField = assistField || (ticket as any)?.assist_field || '';
    if (!currentAssistField) return;
    
    // Split by comma, filter out the name to remove, and rejoin
    const names = currentAssistField.split(',').map((n: string) => n.trim()).filter((n: string) => n !== nameToRemove.trim());
    const newAssistField = names.join(', ');
    setAssistField(newAssistField);
    
    // Update the ticket immediately if it's not a new ticket
    if (!isNewTicket && ticket) {
      updateTicketMutation.mutate({ assist_field: newAssistField });
    }
  };

  useEffect(() => {
    setAppliedAutoMacroIds([]);
  }, [ticketIdForApi]);

  useEffect(() => {
    if (isNewTicket || !ticketIdForApi || isTicketLocked) return;
    const matches = getMatchingAutoMacros().filter((m) => !appliedAutoMacroIds.includes(m.id));
    if (!matches.length) return;
    const joined = matches
      .map((m) => String(m.content || '').trim())
      .filter(Boolean)
      .join('\n\n');
    if (joined) {
      insertMacroContent(joined);
      setAppliedAutoMacroIds((prev) => [...prev, ...matches.map((m) => m.id)]);
      toast.success(`Applied ${matches.length} macro${matches.length > 1 ? 's' : ''} automatically`);
    }
  }, [
    isNewTicket,
    ticketIdForApi,
    isTicketLocked,
    ticketMacros,
    commentType,
    (ticket as any)?.category_id,
    (ticket as any)?.type,
    customValues,
    customFields,
    appliedAutoMacroIds,
  ]);

  const handleAddComment = async () => {
    // Check if comment has content (strip HTML tags for text check)
    const textContent = commentText.replace(/<[^>]*>/g, '').trim();
    if (!textContent && selectedFiles.length === 0) return;
    
    try {
      // Upload files first if any
      const attachmentIds = await uploadFiles();
      
      // Extract mentions from comment text
      const mentionedUserIds = extractMentions(commentText);
      
      // Update assist field if it has changed (user may have manually added/removed people)
      if (assistField && assistField !== ticket?.assist_field) {
        updateTicketMutation.mutate({ assist_field: assistField });
      }
      
      // Then add comment with attachment IDs and mentions
      addCommentMutation.mutate({
        comment: commentText,
        is_internal: commentType === 'internal',
        attachment_ids: attachmentIds,
        mentioned_user_ids: mentionedUserIds,
      });
    } catch (error) {
      // Error already handled in uploadFiles
    }
  };

  const handleQuickAction = (action: string) => {
    if (action === 'escalate') {
      setEscalationDialog(true);
      return;
    }
    if (action === 'solve') {
      setSolveDialog(true);
      return;
    }
    
    const actions = {
      solve: { status: 'solved' },
      close: { status: 'solved' },
      reopen: { status: 'open' },
    };
    
    if (actions[action as keyof typeof actions]) {
      // Update ticket with new action
    }
  };

  const handleConfirmSolve = async () => {
    try {
      // Validate that category and type are set before solving
      if (!ticket?.category_id) {
        toast.error('Please select a category before solving the ticket');
        return;
      }
      if (!ticket?.type) {
        toast.error('Please select a type before solving the ticket');
        return;
      }

      if (solveComment.trim() && ticketIdForApi) {
        await api.post(`/api/tickets/${ticketIdForApi}/comments`, {
          comment: solveComment.trim(),
          is_internal: solveCommentType === 'internal',
        });
      }
      await api.put(`/api/tickets/${ticketIdForApi}`, { status: 'solved' });
      queryClient.invalidateQueries(['ticket', ticketIdentifier]);
      queryClient.invalidateQueries(['ticket-comments', ticketIdForApi]);
      toast.success('Ticket solved');
      setSolveDialog(false);
      setSolveComment('');
      setSolveCommentType('public');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to solve ticket');
    }
  };

  const handleEscalate = () => {
    if (!selectedAgent) {
      toast.error('Please select an agent to escalate to');
      return;
    }
    
    escalationMutation.mutate({
      assigned_to: selectedAgent,
      comment: escalationComment.trim() || undefined
    });
  };

  // Clear validation errors when user starts typing
  const clearValidationError = (field: string) => {
    setValidationErrors(prev => ({ ...prev, [field]: false }));
  };

  const handleCreateTicket = () => {
    // Prevent multiple submissions - check both ref and mutation state
    if (isSubmittingTicket.current || createTicketMutation.isLoading) {
      return;
    }
    
    const errors = [];
    const newValidationErrors = {
      business: false,
      subject: false,
      description: false,
      category: false,
      type: false
    };
    
    if (!selectedBusiness) {
      errors.push('Business selection is required');
      newValidationErrors.business = true;
    }
    if (!newTicketForm.subject?.trim()) {
      errors.push('Subject is required');
      newValidationErrors.subject = true;
    }
    if (!newTicketForm.description?.trim()) {
      errors.push('Description is required');
      newValidationErrors.description = true;
    }
    if (!newTicketForm.category_id) {
      errors.push('Category is required');
      newValidationErrors.category = true;
    }
    if (!newTicketForm.type) {
      errors.push('Type is required');
      newValidationErrors.type = true;
    }
    
    setValidationErrors(newValidationErrors);
    
    if (errors.length > 0) {
      const missingFields = errors.map(error => {
        if (error.includes('Business')) return 'Business';
        if (error.includes('Subject')) return 'Subject';
        if (error.includes('Description')) return 'Description';
        if (error.includes('Category')) return 'Category';
        if (error.includes('Type')) return 'Type';
        return error;
      });
      toast.error(`Please select mandatory fields that are not selected: ${missingFields.join(', ')}`);
      return;
    }
    
    // Set submitting flag to prevent duplicate submissions
    isSubmittingTicket.current = true;
    
    const payload: any = {
      ...newTicketForm,
      business_id: (selectedBusiness as any)?.id,
      created_by: user?.id
    };
    if (callMeQueueIdToLink) {
      payload.call_me_queue_id = callMeQueueIdToLink;
    }

    createTicketMutation.mutate(payload);
  };

  const handleAddFollower = async () => {
    if (!ticket?.id) return;

    try {
      const response = await fetch(`/api/tickets/${ticket.id}/followers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          follower_type: followerType,
          user_id: followerType === 'user' ? followerUserId : null,
          email: followerType === 'email' ? followerEmail : null
        })
      });

      if (response.ok) {
        const result = await response.json();
        setFollowers(prev => [...prev, result.follower]);
        queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
        setAddFollowerDialog(false);
        setFollowerEmail('');
        setFollowerUserId('');
        toast.success('Follower added successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to add follower');
      }
    } catch (error) {
      console.error('Add follower error:', error);
      toast.error('Failed to add follower');
    }
  };

  const handleRemoveFollower = async (followerId: string) => {
    if (!ticket?.id) return;

    try {
      const response = await fetch(`/api/tickets/${ticket.id}/followers/${followerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setFollowers(prev => prev.filter(f => f.id !== followerId));
        queryClient.invalidateQueries(['ticket-audit', ticketIdForApi]);
        toast.success('Follower removed successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove follower');
      }
    } catch (error) {
      console.error('Remove follower error:', error);
      toast.error('Failed to remove follower');
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors = {
      new: 'default' as const,
      open: 'primary' as const,
      pending: 'warning' as const,
      solved: 'success' as const,
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getPriorityColor = (priority: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors = {
      low: 'info' as const,
      medium: 'warning' as const,
      high: 'error' as const,
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  return (
    <div style={{ 
      height: '100%', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: 'white', 
      overflow: isNewTicket ? 'hidden' : 'auto', 
      margin: '-24px', 
      padding: '24px 24px 24px 32px', 
      boxSizing: 'border-box'
    }}>
      <PageHeader
        title={isNewTicket ? 'New Ticket' : null}
        subtitle={isNewTicket ? 'Create a new support ticket' : ''}
        actions={(
          <Space>
            {!isNewTicket && (
              <>
                {tabsContext && (() => {
                  const tabId = getTabIdFromPath(location.pathname);
                  const hasTab = tabId && tabsContext.tabs.some((t) => t.id === tabId);
                  return hasTab ? (
                    <Tooltip title="Close tab">
                      <AntButton
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => {
                          const { tabs, removeTab, switchToTab } = tabsContext;
                          const idx = tabs.findIndex((t) => t.id === tabId);
                          const nextTab = idx > 0 ? tabs[idx - 1] : tabs[idx + 1];
                          removeTab(tabId!);
                          if (nextTab) switchToTab(nextTab.id);
                          else navigate('/tickets');
                        }}
                      />
                    </Tooltip>
                  ) : null;
                })()}
                <AntButton
                  type="text"
                  size="small"
                  icon={<UserOutlined />}
                  onClick={() => setShowContextPanel(!showContextPanel)}
                  style={{ display: window.innerWidth < 1200 ? 'block' : 'none' }}
                />
              </>
            )}
          </Space>
        )}
      />

      {/* Main Content Area */}
      <Flex style={{ 
        flex: 1, 
        overflow: 'hidden', 
        width: '100%', 
        minWidth: 0,
        gap: 0
      }} vertical={window.innerWidth < 1200}>
        {/* Left Panel - Ticket Properties */}
        <Card key="left-panel" 
          style={{ 
            width: isNewTicket ? (window.innerWidth >= 600 ? 400 : '100%') : (window.innerWidth >= 600 ? 280 : '100%'),
            minWidth: isNewTicket ? (window.innerWidth >= 600 ? 400 : 'auto') : (window.innerWidth >= 600 ? 280 : 'auto'),
            maxWidth: isNewTicket ? (window.innerWidth >= 600 ? 500 : '100%') : (window.innerWidth >= 600 ? 320 : '100%'),
            flexShrink: 0,
            borderRight: window.innerWidth >= 600 ? '1px solid #E7E0EC' : 'none',
            borderBottom: window.innerWidth < 600 ? '1px solid #E7E0EC' : 'none',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            order: window.innerWidth < 1200 ? 2 : 1,
            borderRadius: 0,
            boxShadow: 'none'
          }}
        >
          <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, backgroundColor: 'white' }}>
            <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
              Ticket Properties
            </Typography.Title>
            {!isNewTicket && (
              <>
                <Typography.Title level={4} style={{ fontWeight: 700, letterSpacing: 0.5, margin: '0 0 12px 0' }}>
                  Ticket#{ticket?.ticket_number || 'N/A'}
                </Typography.Title>
                <Space size="small" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                      Status
                    </Typography.Text>
                    <AntdSelect
                      value={ticket?.status || 'new'}
                      onChange={(v) => updateTicketMutation.mutate({ status: String(v) })}
                      size="small"
                      style={{ minWidth: 120 }}
                      disabled={isTicketLocked}
                    >
                      {!ticket?.assigned_to && <Option value="new">New</Option>}
                      <Option value="open">Open</Option>
                      <Option value="pending">Pending</Option>
                      <Option value="solved">Solved</Option>
                    </AntdSelect>
                  </div>
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                      Priority
                    </Typography.Text>
                    <AntdSelect
                      value={ticket?.priority || 'medium'}
                      onChange={(v) => updateTicketMutation.mutate({ priority: String(v) })}
                      size="small"
                      style={{ minWidth: 120 }}
                      disabled={isTicketLocked}
                    >
                      <Option value="low">Low</Option>
                      <Option value="medium">Medium</Option>
                      <Option value="high">High</Option>
                    </AntdSelect>
                  </div>
                </Space>
                <Divider style={{ margin: '12px 0' }} />
              </>
            )}
            
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {isNewTicket ? (
                <>
                  {/* Business Selection */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Business *
                    </Typography.Text>
                    <div style={{ position: 'relative' }}>
                      <AutoComplete
                        size="middle"
                        options={(() => {
                          const baseList = businessSearchTerm.length >= 1 ? searchResults : businesses;
                          const toOption = (b: any) => {
                            const address = [b.address, b.city, b.state, b.zip_code].filter(Boolean).join(', ');
                            const mid = b.mid || b.merchant_id || 'N/A';
                            return {
                              value: b.business_name,
                              label: (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <Typography.Text strong style={{ fontSize: '0.875rem' }}>{b.business_name}</Typography.Text>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {address && (
                                      <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
                                        {address}
                                      </Typography.Text>
                                    )}
                                    <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
                                      MID: {mid}
                                    </Typography.Text>
                                  </div>
                                </div>
                              ),
                              business: b
                            };
                          };
                          const opts = baseList.map((b: any) => toOption(b));
                          if (callMePrefill?.business && !opts.some((o: any) => (o as any).business?.id === callMePrefill.business.id)) {
                            opts.unshift(toOption(callMePrefill.business));
                          }
                          return opts;
                        })()}
                        value={selectedBusiness ? (selectedBusiness as any)?.business_name : undefined}
                        onSelect={(value, option) => {
                          const business = (option as any)?.business;
                          setSelectedBusiness(business);
                          setNewTicketForm({ ...newTicketForm, business_id: business?.id || '' });
                          clearValidationError('business');
                        }}
                        onSearch={(text) => {
                          setBusinessSearchTerm(text);
                          clearValidationError('business');
                          // If search is cleared, also clear the selected business
                          if (!text || text.trim() === '') {
                            setSelectedBusiness(null);
                            setNewTicketForm({ ...newTicketForm, business_id: '' });
                          }
                        }}
                        onChange={(value) => {
                          // Handle when the value is cleared via the clear button
                          if (!value || value === '') {
                            setSelectedBusiness(null);
                            setNewTicketForm({ ...newTicketForm, business_id: '' });
                            setBusinessSearchTerm('');
                            clearValidationError('business');
                          }
                        }}
                        style={{ width: '100%' }}
                        placeholder="Search by name, MID, email, phone, or address..."
                        status={validationErrors.business ? 'error' : ''}
                        notFoundContent={businessSearchTerm.length < 1 ? "Type to search..." : <Empty description="No businesses found" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                        allowClear
                        filterOption={false}
                        suffixIcon={isSearching ? <Spin size="small" /> : undefined}
                      />
                    </div>
                    {validationErrors.business && (
                      <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                        {validationErrors.business}
                      </Typography.Text>
                    )}
                  </div>

                  {/* Assignee */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Assignee
                    </Typography.Text>
                    <AntdSelect
                      value={newTicketForm.assigned_to}
                      onChange={(value) => setNewTicketForm({ ...newTicketForm, assigned_to: (value === undefined || value === null || value === '') ? '' : String(value) })}
                      style={{ width: '100%' }}
                      allowClear
                      placeholder="Unassigned"
                      size="middle"
                      showSearch
                      options={[{ value: '', label: 'Unassigned', searchable: '' }, ...assigneeSelectOptionsNew]}
                      filterOption={(input, option: any) => {
                        const searchText = (input || '').toLowerCase().trim();
                        if (!searchText) return true;
                        const optValue = option?.value;
                        if (optValue != null && String(optValue).toLowerCase() === searchText) return true;
                        const searchable = (option?.searchable ?? option?.label ?? '').toLowerCase();
                        return searchable.includes(searchText);
                      }}
                      dropdownStyle={{ minWidth: 300 }}
                      dropdownMatchSelectWidth={false}
                      optionRender={(optOrPayload: any) => {
                        const opt = optOrPayload?.option ?? optOrPayload;
                        const val = opt?.value != null ? String(opt.value) : '';
                        if (!val) return <span>Unassigned</span>;
                        const user = getUserForOption(val, users);
                        if (!user) return <span>{opt?.label ?? 'Unassigned'}</span>;
                        let roleDisplay = user.role_display_name;
                        if (!roleDisplay) {
                          const roleName = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ') : 'Agent';
                          roleDisplay = user.agent_level ? `${roleName} (Level ${user.agent_level})` : roleName;
                        }
                        const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', overflow: 'hidden', padding: '2px 0' }}>
                            <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                            <span style={{ fontSize: '12px', color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleDisplay}</span>
                          </div>
                        );
                      }}
                    >
                    </AntdSelect>
                  </div>

                  {/* Priority */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Priority
                  </Typography.Text>
                  <AntdSelect
                      value={newTicketForm.priority}
                      onChange={(value) => setNewTicketForm({ ...newTicketForm, priority: String(value) })}
                      style={{ width: '100%' }}
                      size="middle"
                    >
                      <Option value="low">Low</Option>
                      <Option value="medium">Medium</Option>
                      <Option value="high">High</Option>
                    </AntdSelect>
                  </div>

                  {/* Category */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Category
                  </Typography.Text>
                  <AntdSelect
                      value={newTicketForm.category_id}
                      onChange={(value) => setNewTicketForm({ ...newTicketForm, category_id: String(value) })}
                      style={{ width: '100%' }}
                      placeholder="Select category"
                      allowClear
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                    >
                      {categories.map((c: any) => (
                        <Option key={c.id} value={c.id}>{c.name}</Option>
                      ))}
                    </AntdSelect>
                  </div>

                  {/* Type */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Type
                  </Typography.Text>
                  <AntdSelect
                      value={newTicketForm.type}
                      onChange={(value) => {
                        setNewTicketForm({ ...newTicketForm, type: value || '' });
                        clearValidationError('type');
                      }}
                      style={{ 
                        width: '100%',
                        borderColor: validationErrors.type ? '#ff4d4f' : undefined 
                      }}
                      placeholder="Select type"
                      allowClear={false}
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                      status={validationErrors.type ? 'error' : undefined}
                    >
                      {ticketTypes.filter((t: any) => t.is_active !== false).map((type: any) => (
                        <Option key={type.id} value={type.name}>{type.name}</Option>
                      ))}
                    </AntdSelect>
                    {validationErrors.type && (
                      <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                        Type is required
                      </Typography.Text>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Collapse
                    ghost
                    defaultActiveKey={['assignment', 'details', 'business']}
                    style={{ background: 'transparent' }}
                    items={[
                      {
                        key: 'assignment',
                        label: <Typography.Text strong style={{ fontSize: 13 }}>Assignment</Typography.Text>,
                        children: (
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {/* Assignee */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Assignee
                  </Typography.Text>
                  <AntdSelect
                      value={ticket?.assigned_to || ''}
                      onChange={(value) => updateTicketMutation.mutate({ assigned_to: (value === undefined || value === null || value === '') ? null : String(value) })}
                      style={{ width: '100%' }}
                      allowClear
                      placeholder="Unassigned"
                      size="middle"
                      showSearch
                      options={[{ value: '', label: 'Unassigned', searchable: '' }, ...assigneeSelectOptionsTicket]}
                      filterOption={(input, option: any) => {
                        const searchText = (input || '').toLowerCase().trim();
                        if (!searchText) return true;
                        const optValue = option?.value;
                        if (optValue != null && String(optValue).toLowerCase() === searchText) return true;
                        const searchable = (option?.searchable ?? option?.label ?? '').toLowerCase();
                        return searchable.includes(searchText);
                      }}
                      disabled={isTicketLocked}
                      dropdownStyle={{ minWidth: 300 }}
                      dropdownMatchSelectWidth={false}
                      optionRender={(optOrPayload: any) => {
                        const opt = optOrPayload?.option ?? optOrPayload;
                        const val = opt?.value != null ? String(opt.value) : '';
                        if (!val) return <span>Unassigned</span>;
                        const user = getUserForOption(val, assigneeOptions);
                        if (!user) return <span>{opt?.label ?? 'Unassigned'}</span>;
                        let roleDisplay = user.role_display_name;
                        if (!roleDisplay) {
                          const roleName = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ') : 'Agent';
                          roleDisplay = user.agent_level ? `${roleName} (Level ${user.agent_level})` : roleName;
                        }
                        const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', overflow: 'hidden', padding: '2px 0' }}>
                            <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                            <span style={{ fontSize: '12px', color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleDisplay}</span>
                          </div>
                        );
                      }}
                    >
                    </AntdSelect>
                  </div>

                  {/* Assist Field */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Assist
                    </Typography.Text>
                    <div style={{ 
                      minHeight: '32px',
                      padding: 8,
                      border: '1px solid #E7E0EC',
                      borderRadius: 8,
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      alignItems: 'center'
                    }}>
                      {assistField ? (
                        assistField.split(',').map((name, index) => {
                          const trimmedName = name.trim();
                          if (!trimmedName) return null;
                          return (
                            <Tag
                              key={index}
                              closable
                              onClose={() => handleRemoveFromAssist(trimmedName)}
                              style={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 500,
                                border: 'none'
                              }}
                            >
                              {trimmedName}
                            </Tag>
                          );
                        })
                      ) : (
                        <Typography.Text type="secondary" style={{ fontStyle: 'italic' }}>
                          No assisting agents
                        </Typography.Text>
                      )}
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      Automatically populated when you @mention agents in comments
                      </Typography.Text>
                  </div>

                  {/* Original Assignee (if escalated) */}
                  {ticket?.original_assignee && ticket?.original_assignee !== ticket?.assigned_to && (
                    <div>
                      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        Originally Assigned To
                      </Typography.Text>
                      <div style={{ 
                        padding: 12, 
                        backgroundColor: '#ff4d4f', 
                        borderRadius: 4, 
                        border: '1px solid',
                        borderColor: '#cf1322',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <UserOutlined style={{ color: 'white', fontSize: 14 }} />
                          <Typography.Text style={{ fontWeight: 500, color: 'white' }}>
                            {ticket.original_assignee_name && ticket.original_assignee_last_name 
                              ? `${ticket.original_assignee_name} ${ticket.original_assignee_last_name}`
                              : ticket.original_assignee_name || 'Unknown User'}
                          </Typography.Text>
                        </div>
                        <Tag 
                          style={{ 
                            alignSelf: 'flex-start',
                            fontSize: '0.7rem', 
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            fontWeight: 'bold',
                            border: 'none',
                            margin: 0
                          }}
                        >
                          Escalated
                        </Tag>
                      </div>
                    </div>
                  )}

                  {/* Followers */}
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Typography.Text type="secondary">
                        Followers
                      </Typography.Text>
                      <AntButton 
                        type="text"
                        size="small" 
                        icon={<UserAddOutlined />}
                        onClick={() => setAddFollowerDialog(true)}
                        style={{ padding: 4 }}
                      />
                    </div>
                    <div style={{ 
                      maxHeight: 120, 
                      overflowY: 'auto', 
                      overflowX: 'hidden',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      alignContent: 'flex-start',
                      width: '100%',
                      minWidth: 0,
                    }}>
                      {followers.length === 0 ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          No followers
                        </Typography.Text>
                      ) : (
                        followers.map((follower) => (
                          <Tag
                            key={follower.id}
                            closable
                            onClose={() => handleRemoveFollower(follower.id)}
                            style={{ 
                              margin: 0,
                              maxWidth: '100%',
                            }}
                          >
                            <Tooltip title={follower.follower_name} placement="top">
                              <span
                                style={{
                                  display: 'inline-block',
                                  maxWidth: 140,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {follower.follower_name}
                              </span>
                            </Tooltip>
                          </Tag>
                        ))
                      )}
                    </div>
                  </div>
                          </Space>
                        ),
                      },
                      {
                        key: 'details',
                        label: <Typography.Text strong style={{ fontSize: 13 }}>Details</Typography.Text>,
                        children: (
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {/* Category */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Category <span style={{ color: 'red' }}>*</span>
                  </Typography.Text>
                  <AntdSelect
                      value={(ticket as any)?.category_id || undefined}
                      onChange={(value) => updateTicketMutation.mutate({ category_id: String(value) })}
                      style={{ width: '100%' }}
                      placeholder="Select category"
                      allowClear
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                      disabled={isTicketLocked}
                    >
                      {categories.map((c: any) => (
                        <Option key={c.id} value={c.id}>{c.name}</Option>
                      ))}
                    </AntdSelect>
                  </div>

                  {/* Type */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Type <span style={{ color: 'red' }}>*</span>
                  </Typography.Text>
                  <AntdSelect
                      value={(ticket as any)?.type || undefined}
                      onChange={(value) => updateTicketMutation.mutate({ type: String(value) })}
                      style={{ width: '100%' }}
                      placeholder="Select type"
                      allowClear
                      size="middle"
                      showSearch
                      optionFilterProp="children"
                      disabled={isTicketLocked}
                    >
                      {ticketTypes.filter((t: any) => t.is_active !== false).map((type: any) => (
                        <Option key={type.id} value={type.name}>{type.name}</Option>
                      ))}
                    </AntdSelect>
                  </div>

                {/* Custom Fields (dynamic) */}
                {customFields.length > 0 && (
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      {/* Additional Fields */}
                    </Typography.Text>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {customFields.map((f: any) => (
                        <div key={f.id}>
                          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            {f.label}{f.required ? ' *' : ''}
                          </Typography.Text>
                          {f.field_type === 'text' && (
                            <AntInput 
                              size="small" 
                              style={{ width: '100%' }}
                              value={customValues[f.id] ?? ''}
                              onChange={(e) => setCustomValues({ ...customValues, [f.id]: e.target.value })}
                              onBlur={(e) => saveCustomFieldImmediate(f.id, e.target.value)}
                              disabled={isTicketLocked}
                            />
                          )}
                          {f.field_type === 'number' && (
                            <AntInput 
                              type="number" 
                              size="small" 
                              style={{ width: '100%' }}
                              value={customValues[f.id] ?? ''}
                              onChange={(e) => setCustomValues({ ...customValues, [f.id]: e.target.value })}
                              onBlur={(e) => saveCustomFieldImmediate(f.id, e.target.value)}
                              disabled={isTicketLocked}
                            />
                          )}
                          {f.field_type === 'checkbox' && (
                            <div style={{ marginTop: 4 }}>
                              <input 
                                type="checkbox" 
                                checked={!!customValues[f.id]} 
                                disabled={isTicketLocked}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  setCustomValues({ ...customValues, [f.id]: checked });
                                await saveCustomFieldImmediate(f.id, checked);
                              }} />
                            </div>
                          )}
                          {f.field_type === 'select' && (
                            <AntdSelect 
                              style={{ width: '100%' }} 
                              size="middle" 
                              value={customValues[f.id] ?? undefined} 
                              onChange={async (v) => {
                                setCustomValues({ ...customValues, [f.id]: v });
                                await saveCustomFieldImmediate(f.id, v);
                              }}
                              showSearch
                              allowClear
                              optionFilterProp="children"
                              placeholder={`Select ${f.label}`}
                              disabled={isTicketLocked}
                            >
                              {(Array.isArray(f.options) ? f.options : []).map((opt: any, idx: number) => (
                                <Option key={idx} value={opt}>{opt}</Option>
                              ))}
                            </AntdSelect>
                          )}
                        </div>
                      ))}
                      {/* Save Fields button intentionally hidden */}
                    </Space>
                  </div>
                )}
                          </Space>
                        ),
                      },
                      {
                        key: 'business',
                        label: <Typography.Text strong style={{ fontSize: 13 }}>Business & Dates</Typography.Text>,
                        children: (
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {/* Business */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Business
                    </Typography.Text>
                    <Typography.Text style={{ fontWeight: 500 }}>
                      {ticket?.business_name || 'N/A'}
                    </Typography.Text>
                  </div>

                  {/* Created Date */}
                  <div>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Created
                    </Typography.Text>
                    <Typography.Text>
                      {ticket?.created_at ? dayjs(ticket.created_at).format('MMM D, YYYY h:mm A') : 'N/A'}
                    </Typography.Text>
                  </div>
                          </Space>
                        ),
                      },
                    ]}
                  />
                </>
              )}
            </Space>
          </div>

        </Card>

        {/* Center Panel - Conversation */}
        <div key="center-panel" style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: 0,
          backgroundColor: 'white', 
          minWidth: isNewTicket ? (window.innerWidth >= 600 ? 500 : '100%') : (window.innerWidth >= 600 ? (window.innerWidth >= 960 ? 600 : 400) : '100%'),
          maxWidth: 'none',
          order: window.innerWidth < 600 ? 1 : 2,
          overflow: 'hidden'
        }}>
          {/* Conversation Header - Only for existing tickets */}
          {!isNewTicket && (
            <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
              <GrammarCheckedTextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                value={editableSubject}
                onValueChange={(value) => setEditableSubject(value)}
                onBlur={handleSubjectBlur}
                placeholder="Enter ticket subject"
                style={{ width: '100%' }}
              />
              <Typography.Text type="secondary" style={{ marginTop: 4, display: 'block', fontSize: 12 }}>
                {ticket?.created_at ? dayjs(ticket.created_at).fromNow() : 'Unknown'}
              </Typography.Text>
            </div>
          )}

          {/* New Ticket Form */}
          {isNewTicket && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'white' }}>
              {/* Action Buttons - Moved to top */}
              <div style={{ 
                padding: 24, 
                borderBottom: '1px solid #E7E0EC',
                backgroundColor: '#fafafa',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 16,
                flexShrink: 0
              }}>
                <AntButton 
                  onClick={() => {
                    // Check if there's a returnTo state from navigation
                    const returnTo = location.state?.returnTo;
                    if (returnTo) {
                      navigate(returnTo);
                    } else {
                      navigate('/tickets');
                    }
                  }}
                  disabled={createTicketMutation.isLoading}
                  style={{ minWidth: 100 }}
                >
                  Cancel
                </AntButton>
                <AntButton 
                  type="primary"
                  onClick={handleCreateTicket}
                  loading={createTicketMutation.isLoading}
                  disabled={createTicketMutation.isLoading}
                  style={{ minWidth: 100 }}
                >
                  Create Ticket
                </AntButton>
              </div>

              {/* Subject Field */}
              <div style={{ padding: 24, borderBottom: '1px solid #E7E0EC', backgroundColor: 'white' }}>
                <Typography.Text type="secondary" style={{ marginBottom: 12, fontWeight: 500, display: 'block' }}>
                  Subject *
                </Typography.Text>
                <GrammarCheckedTextArea
                  style={{ width: '100%' }}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  value={newTicketForm.subject}
                  onValueChange={(value) => {
                    setNewTicketForm({ ...newTicketForm, subject: value });
                    clearValidationError('subject');
                  }}
                  placeholder="Enter ticket subject..."
                  status={validationErrors.subject ? 'error' : ''}
                />
                {validationErrors.subject && (
                  <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    Subject is required
                  </Typography.Text>
                )}
              </div>

              {/* Description Field */}
              <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column', borderBottom: '1px solid #E7E0EC', backgroundColor: 'white', minHeight: 200 }}>
                <Typography.Text type="secondary" style={{ marginBottom: 12, fontWeight: 500, display: 'block' }}>
                  Description *
                </Typography.Text>
                <GrammarCheckedTextArea
                  style={{ width: '100%' }}
                  autoSize={{ minRows: 8, maxRows: 20 }}
                  value={newTicketForm.description || ''}
                  onValueChange={(value) => {
                    setNewTicketForm({ ...newTicketForm, description: value });
                    clearValidationError('description');
                  }}
                  placeholder="Type your description here..."
                  status={validationErrors.description ? 'error' : ''}
                />
                {validationErrors.description && (
                  <Typography.Text type="danger" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    Description is required
                  </Typography.Text>
                )}
              </div>

              {/* Recent Tickets Section */}
              {selectedBusiness && isNewTicket && (
                <div style={{ 
                  padding: '24px 24px 24px 24px', 
                  borderTop: '1px solid #E7E0EC',
                  backgroundColor: '#fafafa',
                  borderBottom: '1px solid #E7E0EC',
                  marginBottom: 0
                }}>
                  <Typography.Text type="secondary" style={{ marginBottom: 12, fontWeight: 500, display: 'block' }}>
                    Recent Tickets for {selectedBusiness?.business_name || 'Selected Business'}
                  </Typography.Text>
                  {recentTicketsLoading ? (
                    <Spin size="small" />
                  ) : recentTicketsData && recentTicketsData.length > 0 ? (
                    <div style={{ marginBottom: 0 }}>
                      <Table
                        dataSource={recentTicketsData}
                        columns={[
                          {
                            title: 'Ticket #',
                            dataIndex: 'ticket_number',
                            key: 'ticket_number',
                            width: 120,
                            render: (text: string) => (
                              <Typography.Text strong style={{ color: '#1890ff', cursor: 'pointer' }}>
                                #{text}
                              </Typography.Text>
                            ),
                          },
                          {
                            title: 'Subject',
                            dataIndex: 'subject',
                            key: 'subject',
                            ellipsis: true,
                            render: (text: string) => (
                              <Typography.Text ellipsis style={{ maxWidth: 300 }}>
                                {text}
                              </Typography.Text>
                            ),
                          },
                          {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            width: 100,
                            render: (status: string) => (
                              <Tag color={
                                status === 'solved' ? 'green' :
                                status === 'pending' ? 'orange' :
                                status === 'open' ? 'blue' : 'default'
                              }>
                                {status}
                              </Tag>
                            ),
                          },
                          {
                            title: 'Priority',
                            dataIndex: 'priority',
                            key: 'priority',
                            width: 100,
                            render: (priority: string) => (
                              <Tag color={
                                priority === 'urgent' ? 'red' :
                                priority === 'high' ? 'orange' :
                                priority === 'medium' ? 'blue' : 'default'
                              }>
                                {priority}
                              </Tag>
                            ),
                          },
                          {
                            title: 'Created',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            width: 150,
                            render: (date: string) => (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(date).format('MMM D, YYYY h:mm A')}
                              </Typography.Text>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="id"
                        style={{ marginBottom: 0 }}
                        onRow={(record: any) => ({
                          onClick: () => {
                            setSelectedTicketForModal(record);
                            setRecentTicketsModalVisible(true);
                          },
                          style: { cursor: 'pointer' },
                          onMouseEnter: (e: React.MouseEvent<HTMLTableRowElement>) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          },
                          onMouseLeave: (e: React.MouseEvent<HTMLTableRowElement>) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          },
                        })}
                      />
                    </div>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      No recent tickets found for this business.
                    </Typography.Text>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Message Composer - Moved to Top */}
          {!isNewTicket && (
            <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC', backgroundColor: '#FEFBFF' }}>

              <div style={{ position: 'relative', marginBottom: 16 }}>
                {/* Tiptap Rich Text Editor - wrapped to isolate editor crashes */}
                <CommentsErrorBoundary fallback={
                  <div style={{ minHeight: 160, padding: 24, background: '#fafafa', borderRadius: 8, textAlign: 'center' }}>
                    <Typography.Text type="secondary">Editor failed to load. You can still view the ticket.</Typography.Text>
                  </div>
                }>
                <div style={{ 
                  position: 'relative',
                  border: '1px solid #E7E0EC',
                  borderRadius: 8,
                  backgroundColor: commentType === 'internal' ? 'rgba(255, 152, 0, 0.05)' : 'white',
                  minHeight: 160,
                  maxHeight: 400,
                  overflow: showMentionDropdown ? 'visible' : 'auto'
                }}>
                  {editor && (
                    <>
                      {/* Enhanced Toolbar */}
                      <div style={{
                        borderBottom: '1px solid #E7E0EC',
                        padding: 8,
                        display: 'flex',
                        gap: 4,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        backgroundColor: '#fafafa',
                        borderRadius: '8px 8px 0 0'
                      }}>
                        <Tooltip title="Bold">
                          <AntButton
                            type={editor.isActive('bold') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            disabled={isTicketLocked}
                            style={{ fontWeight: 'bold' }}
                          >
                            B
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Italic">
                          <AntButton
                            type={editor.isActive('italic') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            disabled={isTicketLocked}
                            style={{ fontStyle: 'italic' }}
                          >
                            I
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Underline">
                          <AntButton
                            type={editor.isActive('underline') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            disabled={isTicketLocked}
                            style={{ textDecoration: 'underline' }}
                          >
                            U
                          </AntButton>
                        </Tooltip>
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Tooltip title="Bullet List">
                          <AntButton
                            type={editor.isActive('bulletList') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            disabled={isTicketLocked}
                          >
                            <span style={{ fontSize: '16px' }}>•</span>
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Numbered List">
                          <AntButton
                            type={editor.isActive('orderedList') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            disabled={isTicketLocked}
                          >
                            <span style={{ fontSize: '14px' }}>1.</span>
                          </AntButton>
                        </Tooltip>
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Tooltip title="Strikethrough">
                          <AntButton
                            type={editor.isActive('strike') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                            disabled={isTicketLocked}
                            style={{ textDecoration: 'line-through' }}
                          >
                            S
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Code">
                          <AntButton
                            type={editor.isActive('code') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleCode().run()}
                            disabled={isTicketLocked}
                            icon={<CodeOutlined />}
                          />
                        </Tooltip>
                        <Tooltip title="Blockquote">
                          <AntButton
                            type={editor.isActive('blockquote') ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                            disabled={isTicketLocked}
                          >
                            "
                          </AntButton>
                        </Tooltip>
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Tooltip title="Heading 1">
                          <AntButton
                            type={editor.isActive('heading', { level: 1 }) ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                            disabled={isTicketLocked}
                          >
                            H1
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Heading 2">
                          <AntButton
                            type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                            disabled={isTicketLocked}
                          >
                            H2
                          </AntButton>
                        </Tooltip>
                        <Tooltip title="Heading 3">
                          <AntButton
                            type={editor.isActive('heading', { level: 3 }) ? 'primary' : 'text'}
                            size="small"
                            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                            disabled={isTicketLocked}
                          >
                            H3
                          </AntButton>
                        </Tooltip>
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Tooltip title="Add/Edit Link">
                          <AntButton
                            type={editor.isActive('link') ? 'primary' : 'text'}
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() => {
                              const previousUrl = editor.getAttributes('link').href;
                              const url = window.prompt('Enter URL:', previousUrl || '');
                              if (url === null) return;
                              
                              if (url === '') {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                              } else if (url) {
                                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                              }
                            }}
                            disabled={isTicketLocked}
                          />
                        </Tooltip>
                        <Divider type="vertical" style={{ margin: '0 4px' }} />
                        <Tooltip title="Insert Emoji">
                          <AntButton
                            type={showEmojiPicker ? 'primary' : 'text'}
                            size="small"
                            icon={<SmileOutlined />}
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            disabled={isTicketLocked}
                          />
                        </Tooltip>
                        <Tooltip title="Attach File">
                          <AntButton
                            type="text"
                            size="small"
                            icon={<PaperClipOutlined />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isTicketLocked}
                          />
                        </Tooltip>
                        {isCommentGrammarInitialized && (
                          <Divider type="vertical" style={{ margin: '0 4px' }} />
                        )}
                        {isCommentGrammarInitialized && (
                          isCheckingCommentGrammar ? (
                            <Spin size="small" />
                          ) : commentGrammarIssues.length > 0 ? (
                            <Popover
                              content={
                                <div style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
                                  <Typography.Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
                                    Grammar Issues ({commentGrammarIssues.length})
                                  </Typography.Title>
                                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    {commentGrammarIssues.map((issue, index) => (
                                      <div
                                        key={index}
                                        style={{
                                          padding: 8,
                                          backgroundColor: '#fff7e6',
                                          borderRadius: 4,
                                          border: '1px solid #ffd591',
                                        }}
                                      >
                                        <Typography.Text strong style={{ fontSize: 12 }}>
                                          Position {issue.start}-{issue.end}
                                        </Typography.Text>
                                        <div style={{ marginTop: 4 }}>
                                          <Typography.Text style={{ fontSize: 12 }}>{issue.message}</Typography.Text>
                                        </div>
                                        {issue.suggestions.length > 0 && (
                                          <div style={{ marginTop: 8 }}>
                                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>Suggestions:</Typography.Text>
                                            <div style={{ marginTop: 4 }}>
                                              {issue.suggestions.slice(0, 3).map((suggestion, sIndex) => (
                                                <AntButton
                                                  key={sIndex}
                                                  type="link"
                                                  size="small"
                                                  style={{ padding: '2px 4px', fontSize: 11, height: 'auto', display: 'block', textAlign: 'left' }}
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!editor || !applyCommentSuggestion) return;
                                                    
                                                    try {
                                                      const newText = await applyCommentSuggestion(issue, sIndex);
                                                      if (newText && newText !== commentPlainText) {
                                                        // Apply the suggestion by replacing the text in the editor
                                                        // We need to replace the range from issue.start to issue.end with the suggestion
                                                        const currentText = editor.getText();
                                                        
                                                        // Calculate the replacement: take text before issue, add suggestion, add text after issue
                                                        const textBefore = currentText.substring(0, issue.start);
                                                        const textAfter = currentText.substring(issue.end);
                                                        const replacementText = textBefore + suggestion + textAfter;
                                                        
                                                        // Set the new content (as plain text - formatting will be lost, but it's better than nothing)
                                                        // Actually, let's try to preserve HTML structure by using editor commands
                                                        // But for simplicity, we'll replace the problematic range
                                                        editor.chain()
                                                          .focus()
                                                          .setTextSelection({ from: issue.start, to: issue.end })
                                                          .deleteSelection()
                                                          .insertContent(suggestion)
                                                          .run();
                                                        
                                                        setShowCommentGrammarIssues(false);
                                                      }
                                                    } catch (error) {
                                                      console.error('Failed to apply suggestion:', error);
                                                    }
                                                  }}
                                                >
                                                  • {suggestion}
                                                </AntButton>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </Space>
                                </div>
                              }
                              title={null}
                              trigger="click"
                              open={showCommentGrammarIssues}
                              onOpenChange={setShowCommentGrammarIssues}
                              placement="topRight"
                            >
                              <Tooltip title={`${commentGrammarIssues.length} grammar issue${commentGrammarIssues.length > 1 ? 's' : ''} found`}>
                                <AntButton
                                  type="text"
                                  size="small"
                                  icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                                  disabled={isTicketLocked}
                                />
                              </Tooltip>
                            </Popover>
                          ) : commentPlainText.trim() ? (
                            <Tooltip title="No grammar issues">
                              <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 4 }} />
                            </Tooltip>
                          ) : null
                        )}
                      </div>
                      
                      {/* Editor Content - guard: useEditor can return null initially; defer mount to avoid render storm */}
                      {editorMounted && editor ? (
                        <>
                          <EditorContent editor={editor} />
                          {editor.isEmpty && (
                        <div style={{
                          position: 'absolute',
                          top: 50,
                          left: 16,
                          color: '#bfbfbf',
                          fontStyle: 'italic',
                          pointerEvents: 'none'
                        }}>
                          {commentType === 'public' ? "Type your reply... Use @ to mention agents" : "Add internal note... Use @ to mention agents"}
                        </div>
                          )}
                        </>
                      ) : (
                        <div style={{ minHeight: 160, padding: 16, background: '#fafafa', borderRadius: 4, color: '#bfbfbf' }}>
                          Loading editor...
                        </div>
                      )}
                    </>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    multiple
                    onChange={handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.xls"
                  />
                </div>
                </CommentsErrorBoundary>
                
                {/* Tiptap Styles */}
                <style>{`
                  .tiptap-editor {
                    min-height: 160px;
                    max-height: 400px;
                    overflow-y: auto;
                    padding: 16px;
                    font-size: 14px;
                    line-height: 1.6;
                  }
                  .tiptap-editor.internal-comment {
                    background-color: rgba(255, 152, 0, 0.05);
                  }
                  .tiptap-editor p {
                    margin: 0.5em 0;
                  }
                  .tiptap-editor p:first-child {
                    margin-top: 0;
                  }
                  .tiptap-editor p:last-child {
                    margin-bottom: 0;
                  }
                  .tiptap-editor .mention {
                    background-color: #e3f2fd;
                    color: #1976d2;
                    padding: 2px 4px;
                    border-radius: 4px;
                    font-weight: 500;
                  }
                  .tiptap-editor:focus {
                    outline: none;
                  }
                  
                  /* Style mentions in saved comments */
                  span.mention,
                  span[data-type="mention"],
                  span[data-user-id] {
                    background-color: #e3f2fd !important;
                    color: #1976d2 !important;
                    padding: 2px 4px !important;
                    border-radius: 4px !important;
                    font-weight: 500 !important;
                    display: inline-block;
                  }
                  
                  /* Style images in editor and saved comments */
                  .tiptap-editor img,
                  img {
                    max-width: 100% !important;
                    height: auto !important;
                    border-radius: 8px !important;
                    margin: 8px 0 !important;
                    display: block !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
                  }
                `}</style>
                
                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    backgroundColor: 'white',
                    border: '1px solid #E7E0EC',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    padding: 16,
                    maxHeight: 200,
                    overflow: 'auto',
                    marginTop: 8,
                  }}>
                    <Typography.Text style={{ fontWeight: 600, marginBottom: 12, color: '#1976d2', display: 'block' }}>
                      Choose an emoji:
                    </Typography.Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {[
                        '😊', '😄', '😃', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
                        '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
                        '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
                        '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏',
                        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
                        '🎉', '🎊', '🎈', '🎁', '🎀', '🎂', '🍰', '🧁', '🍭', '🍬', '🍫', '🍩', '🍪', '🎂', '🍰', '🧁', '🍭', '🍬', '🍫', '🍩',
                        '💻', '🖥️', '🖨️', '⌨️', '🖱️', '🖲️', '💽', '💾', '💿', '📀', '📱', '📲', '☎️', '📞', '📟', '📠', '🔋', '🔌', '💡', '🔦',
                        '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬', '📭', '📮', '🗳️', '✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📝', '💼',
                        '🏢', '🏬', '🏭', '🏗️', '🏘️', '🏚️', '🏠', '🏡', '🏛️', '🏜️', '🏝️', '🏞️', '🏟️', '🏛️', '🏗️', '🧱', '🏗️', '🏘️', '🏚️', '🏠',
                        '👥', '👤', '👨‍💼', '👩‍💼', '👨‍💻', '👩‍💻', '👨‍🎓', '👩‍🎓', '👨‍🏫', '👩‍🏫', '👨‍⚕️', '👩‍⚕️', '👨‍⚖️', '👩‍⚖️', '👨‍✈️', '👩‍✈️', '👨‍🔬', '👩‍🔬', '👨‍🔧', '👩‍🔧'
                      ].map((emoji) => (
                        <AntButton
                          key={emoji}
                          size="small"
                          type="text"
                          onClick={() => insertEmoji(emoji)}
                          style={{ 
                            padding: 4, 
                            fontSize: '1.2rem',
                            borderRadius: 4,
                            minWidth: 'auto',
                            height: 'auto',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f0f0f0';
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          {emoji}
                        </AntButton>
                      ))}
                    </div>
                  </div>
                )}


                {/* Link Dialog - Temporarily hidden */}
                {false && showLinkDialog && (
                  <div style={{
                    position: 'absolute',
                    top: -150,
                    left: 0,
                    right: 0,
                    zIndex: 1001,
                    backgroundColor: 'white',
                    border: '1px solid #E7E0EC',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    padding: 16,
                  }}>
                    <Typography.Text style={{ marginBottom: 8, display: 'block', fontWeight: 600 }}>
                      Insert Link
                    </Typography.Text>
                    <AntInput
                      style={{ width: '100%', marginBottom: 8 }}
                      size="small"
                      placeholder="Link text"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                    />
                    <AntInput
                      style={{ width: '100%', marginBottom: 8 }}
                      size="small"
                      placeholder="URL (https://...)"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <AntButton size="small" onClick={() => setShowLinkDialog(false)}>
                        Cancel
                      </AntButton>
                      <AntButton type="primary" size="small" onClick={insertLink}>
                        Insert
                      </AntButton>
                    </div>
                  </div>
                )}
                
                {/* Mention Dropdown */}
                {showMentionDropdown && (
                  <div
                    data-mention-dropdown
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #E7E0EC',
                      borderRadius: 8,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                      maxHeight: 200,
                      overflow: 'auto',
                      marginTop: 8,
                    }}
                  >
                    {/* Header */}
                    <div style={{ 
                      padding: 12, 
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: '#fafafa',
                      borderRadius: '12px 12px 0 0'
                    }}>
                      <Typography.Text style={{ 
                        fontWeight: 600, 
                        color: '#1976d2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <UserOutlined style={{ fontSize: 14 }} />
                        Mention someone
                      </Typography.Text>
                    </div>

                    {/* User List */}
                    {getFilteredUsers().length > 0 ? (
                      getFilteredUsers().map((user: any, index: number) => (
                        <div
                          key={user.id}
                          onClick={() => {
                            handleRichMentionSelect(user);
                          }}
                          style={{
                            padding: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            transition: 'all 0.2s ease',
                            backgroundColor: index === selectedMentionIndex ? '#e3f2fd' : 'transparent',
                            borderBottom: index < getFilteredUsers().length - 1 ? '1px solid #f0f0f0' : 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (index !== selectedMentionIndex) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                            e.currentTarget.style.transform = 'translateX(2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = index === selectedMentionIndex ? '#e3f2fd' : 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          {/* Avatar */}
                          <Avatar 
                            size={32}
                            style={{ 
                              backgroundColor: user.role === 'admin' ? '#1976d2' : user.role === 'agent' ? '#4caf50' : '#ff9800',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                          >
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </Avatar>
                          
                          {/* User Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography.Text style={{ 
                              fontWeight: 600,
                              color: '#333',
                              lineHeight: 1.2,
                              display: 'block'
                            }}>
                              {user.first_name} {user.last_name}
                            </Typography.Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                              <Tag 
                                style={{ 
                                  height: 16,
                                  fontSize: '0.65rem',
                                  backgroundColor: user.role === 'admin' ? '#e3f2fd' : user.role === 'agent' ? '#e8f5e8' : '#fff3e0',
                                  color: user.role === 'admin' ? '#1976d2' : user.role === 'agent' ? '#4caf50' : '#ff9800',
                                  fontWeight: 500,
                                  border: 'none',
                                  padding: '0 4px',
                                  lineHeight: '16px'
                                }}
                              >
                                {user.role}
                              </Tag>
                              {user.agent_level && (
                                <Tag 
                                  style={{ 
                                    height: 16,
                                    fontSize: '0.65rem',
                                    borderColor: '#ddd',
                                    color: '#666',
                                    padding: '0 4px',
                                    lineHeight: '16px'
                                  }}
                                >
                                  Level {user.agent_level}
                                </Tag>
                              )}
                            </div>
                          </div>
                          
                          {/* Mention Icon */}
                          <div style={{ 
                            opacity: index === selectedMentionIndex ? 1 : 0.6,
                            transition: 'opacity 0.2s ease',
                            color: index === selectedMentionIndex ? '#1976d2' : 'inherit'
                          }}>
                            {index === selectedMentionIndex ? (
                              <CheckCircleOutlined style={{ fontSize: 16, color: '#1976d2' }} />
                            ) : (
                              <UserAddOutlined style={{ fontSize: 16 }} />
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ 
                        padding: 16, 
                        textAlign: 'center',
                        color: '#666'
                      }}>
                        <UserOutlined style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }} />
                        <Typography.Text type="secondary" style={{ display: 'block' }}>
                          No users found for "{mentionQuery}"
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                          Try typing a different name
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* File attachments */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Typography.Text type="secondary" style={{ marginBottom: 8, display: 'block', fontSize: 12 }}>
                    Attachments ({selectedFiles.length})
                  </Typography.Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {selectedFiles.map((file, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => removeFile(index)}
                        style={{ marginRight: 4, marginBottom: 4 }}
                        icon={file.type.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
                      >
                        {file.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {/* Comment Type Selector and Send Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <AntdSelect
                  placeholder="Insert macro"
                  value={selectedMacroId}
                  onChange={(value) => {
                    const selected = (ticketMacros || []).find((m) => m.id === value);
                    if (selected) {
                      insertMacroContent(selected.content);
                    }
                    setSelectedMacroId(undefined);
                  }}
                  style={{ minWidth: 260, borderRadius: 8 }}
                  size="small"
                  disabled={isTicketLocked}
                  allowClear
                  options={(ticketMacros || [])
                    .filter((m) => m.is_active && (m.apply_to_comment_type === 'both' || m.apply_to_comment_type === commentType))
                    .map((m) => ({ value: m.id, label: m.title }))}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AntdSelect
                    value={commentType}
                    onChange={(value) => setCommentType(String(value))}
                    style={{ minWidth: 120, borderRadius: 8 }}
                    size="small"
                    disabled={isTicketLocked}
                  >
                    <Option value="public">Public</Option>
                    <Option value="internal">Internal</Option>
                  </AntdSelect>
                  <AntButton
                    type="primary"
                    onClick={handleAddComment}
                    disabled={isTicketLocked || (!commentText.replace(/<[^>]*>/g, '').trim() && selectedFiles.length === 0) || addCommentMutation.isLoading || uploadingFiles}
                    icon={<SendOutlined />}
                    loading={uploadingFiles || addCommentMutation.isLoading}
                    style={{ borderRadius: 8 }}
                  >
                    {uploadingFiles ? 'Uploading...' : 'Send'}
                  </AntButton>
                </div>
              </div>
            </div>
          )}

          {/* Locked Ticket Alert - Above Comments */}
          {!isNewTicket && isTicketLocked && (
            <div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}>
              <Alert 
                type="warning"
                message="Ticket Locked"
                description="This ticket has been solved for 7+ days and is locked. Please create a follow-up ticket instead."
                action={
                  <AntButton 
                    size="small" 
                    type="primary"
                    onClick={() => setFollowUpDialogOpen(true)}
                  >
                    Create Follow-up
                  </AntButton>
                }
                showIcon
                style={{ maxWidth: 800, width: '100%' }}
              />
            </div>
          )}

          {/* Conversation Thread - scrollable so comments are not cut off */}
          {!isNewTicket && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: window.innerWidth >= 600 ? 16 : 12 }}>
              {/* Admin-only Audit Logs Modal Triggered Elsewhere */}
              {commentsLoading ? (
                <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: 40 }} />
              ) : (
                <CommentsErrorBoundary fallback={
                  <div style={{ padding: 24, textAlign: 'center' }}>
                    <Typography.Text type="secondary">Failed to load comments.</Typography.Text>
                  </div>
                }>
                <List
                  dataSource={comments.filter(Boolean).slice().reverse()}
                  style={{ width: '100%' }}
                  locale={{ emptyText: '' }}
                  renderItem={(comment: any, index: number) => {
                  if (!comment) return null;
                  const isInternal = Boolean(comment.is_internal);
                  const authorFirst = comment?.user?.first_name ?? comment?.first_name ?? 'U';
                  const authorLast = comment?.user?.last_name ?? comment?.last_name ?? '';
                  return (
                    <List.Item key={comment.id ?? `comment-${index}`} style={{ marginBottom: 16, alignItems: 'flex-start', padding: 0, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 12 }}>
                        <Avatar style={{ backgroundColor: isInternal ? '#faad14' : '#1890ff', flexShrink: 0, width: 40, height: 40 }}>
                          {authorFirst?.[0] || 'U'}
                        </Avatar>
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            width: '100%',
                            border: '1px solid',
                            borderColor: isInternal ? '#faad14' : '#d9d9d9',
                            backgroundColor: isInternal ? 'rgba(255, 183, 77, 0.12)' : '#ffffff',
                            borderRadius: 8,
                            padding: '12px 16px',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <Space size="small">
                              <Typography.Text style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {authorFirst} {authorLast}
                              </Typography.Text>
                              {isInternal && (
                                <Tag color="warning" style={{ height: 20, margin: 0, fontSize: '0.75rem', lineHeight: '20px', padding: '0 6px' }}>
                                  Internal note
                                </Tag>
                              )}
                            </Space>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {(() => { try { return comment?.created_at ? dayjs(comment.created_at).format('MMM D, YYYY h:mm A') : 'Unknown'; } catch { return 'Unknown'; } })()}
                            </Typography.Text>
                          </div>
                          <div 
                            style={{ 
                              whiteSpace: 'pre-wrap',
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              color: '#262626',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: String(comment.content || comment.comment || '<span style="color: #bfbfbf; font-style: italic;">No content</span>')
                            }}
                          />
                          
                          {/* Display attachments */}
                          {comment.attachments && comment.attachments.length > 0 && (
                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                              <Typography.Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block', fontWeight: 500 }}>
                                Attachments:
                              </Typography.Text>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {comment.attachments.map((attachment: any, attIdx: number) => (
                                  <Tag
                                    key={attachment?.id ?? `att-${attIdx}`}
                                    onClick={() => {
                                      // Create a blob URL for authenticated access
                                      const token = localStorage.getItem('token');
                                      fetch(`/api/attachments/${attachment.filename}`, {
                                        headers: {
                                          'Authorization': `Bearer ${token}`
                                        }
                                      })
                                      .then(response => response.blob())
                                      .then(blob => {
                                        const url = window.URL.createObjectURL(blob);
                                        window.open(url, '_blank');
                                        // Clean up the blob URL after a delay
                                        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                      })
                                      .catch(error => {
                                        console.error('Error opening attachment:', error);
                                        toast.error('Failed to open attachment');
                                      });
                                    }}
                                    icon={attachment.mime_type?.startsWith('image/') ? <FileImageOutlined /> : <FilePdfOutlined />}
                                    style={{ cursor: 'pointer', margin: 0 }}
                                  >
                                    {attachment.original_filename}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
                </CommentsErrorBoundary>
              )}
            </div>
          )}

        </div>

        {/* Right Panel - Customer Context - Hidden for New Ticket */}
        {!isNewTicket && (
        <Card key="right-panel"
          style={{ 
            width: window.innerWidth >= 1200 ? 320 : '100%',
            minWidth: window.innerWidth >= 1200 ? 300 : 'auto',
            maxWidth: window.innerWidth >= 1200 ? 360 : '100%',
            flex: window.innerWidth >= 1200 ? '0 0 320px' : 'none',
            borderLeft: window.innerWidth >= 1200 ? '1px solid #E7E0EC' : 'none',
            borderTop: window.innerWidth < 1200 ? '1px solid #E7E0EC' : 'none',
            backgroundColor: 'white',
            display: showContextPanel || window.innerWidth >= 1200 ? 'flex' : 'none',
            flexDirection: 'column',
            order: window.innerWidth < 1200 ? 3 : 3,
            borderRadius: 0,
            boxShadow: 'none'
          }}
        >
          {/* Service banner(s) - large tag when business has a service with show_banner */}
          {!isNewTicket && (() => {
            const bannerServices = (Array.isArray(businessServices) ? businessServices : []).filter((svc: any) => svc?.show_banner);
            if (bannerServices.length === 0) return null;
            const labelMap: Record<string, string> = {
              retailzpos: 'RetailZPOS',
              ptechpos: 'PTechPOS',
              moolah_points: 'Moolah Points',
              patelhms: 'Patel HMS',
              credit_card_processing: 'Credit Card Processing',
            };
            return (
              <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
                {bannerServices.map((svc: any) => {
                  const type = svc?.service_type || svc;
                  const label = labelMap[type] || (typeof type === 'string' ? type.replace(/_/g, ' ') : type);
                  const bgColor = svc?.banner_color || '#1890ff';
                  return (
                    <div
                      key={type}
                      style={{
                        background: bgColor,
                        color: '#fff',
                        padding: '12px 16px',
                        borderRadius: 8,
                        marginBottom: 8,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Customer Information - Only show when not creating new ticket */}
          {!isNewTicket && (
          <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
            <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
              Customer Information
            </Typography.Title>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <Avatar style={{ backgroundColor: '#1890ff' }}>
                {ticket?.business_name?.[0] || 'B'}
              </Avatar>
              <div>
                <Typography.Text style={{ fontWeight: 600, display: 'block' }}>
                  {ticket?.business_name || 'N/A'}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {ticket?.contact_person || 'N/A'}
                </Typography.Text>
              </div>
            </div>

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {/* MID */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text type="secondary">
                  MID
                </Typography.Text>
                <Typography.Text style={{ fontWeight: 600 }}>
                  {(ticket as any)?.mid || (businessDetail as any)?.mid || 'N/A'}
                </Typography.Text>
              </div>
              {/* Portal */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text type="secondary">
                  Portal
                </Typography.Text>
                <Typography.Text style={{ fontWeight: 600 }}>
                  {(ticket as any)?.retailzpos_portal || (businessDetail as any)?.retailzpos_portal || 'Not set'}
                </Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MailOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                <Typography.Text style={{ fontSize: '0.85rem' }}>{ticket?.business_email || 'N/A'}</Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                <Typography.Text style={{ fontSize: '0.85rem' }}>{ticket?.business_phone || 'N/A'}</Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <EnvironmentOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                <Typography.Text style={{ fontSize: '0.85rem' }}>
                  {(() => {
                    const t: any = ticket || {};
                    const b: any = businessDetail || {};
                    const addr = t.address || b.address;
                    const city = t.city || b.city;
                    const state = t.state || b.state;
                    const zip = t.zip_code || b.zip_code;
                    const loc = [city, state].filter(Boolean).join(', ');
                    const locZip = [loc, zip].filter(Boolean).join(' ');
                    const full = [addr, locZip].filter(Boolean).join(' ');
                    return full || 'N/A';
                  })()}
                </Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />
                <Typography.Text style={{ fontSize: '0.85rem' }}>
                  {dayjs().format('ddd, HH:mm')} Local Time
                </Typography.Text>
              </div>
            </Space>
          </div>
          )}

          {/* Ticket history for this business - below Customer Information, before Services */}
          {!isNewTicket && ticket?.business_id && (
            <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
              <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 12, marginTop: 0 }}>
                Ticket history for this business
              </Typography.Title>
              {businessTicketsLoading ? (
                <Spin size="small" />
              ) : businessTicketHistory.length > 0 ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    {businessTicketHistory.slice(0, 5).map((t: any) => (
                      <div key={t.id} style={{ marginBottom: 10 }}>
                        <Typography.Link
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/workspace/number/${t.ticket_number}`, {
                              state: { openInNewTab: true },
                            });
                          }}
                          style={{ fontSize: 13, fontWeight: 500 }}
                        >
                          #{t.ticket_number}
                        </Typography.Link>
                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>
                          {t.subject}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                  <AntButton
                    type="default"
                    size="small"
                    block
                    onClick={() => setBusinessTicketsModalVisible(true)}
                  >
                    View All Tickets For This Business
                  </AntButton>
                </>
              ) : (
                <>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                    No other tickets for this business.
                  </Typography.Text>
                  <AntButton
                    type="default"
                    size="small"
                    block
                    onClick={() => setBusinessTicketsModalVisible(true)}
                  >
                    View All Tickets For This Business
                  </AntButton>
                </>
              )}
            </div>
          )}

          {/* Modal: All tickets for this business - status, date created, assignee; click # opens workspace tab */}
          <Modal
            title={`All tickets for ${ticket?.business_name || 'this business'}`}
            open={businessTicketsModalVisible}
            onCancel={() => setBusinessTicketsModalVisible(false)}
            footer={null}
            width={620}
            destroyOnClose
          >
            {businessTicketsAllLoading ? (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : businessTicketsAllList.length === 0 ? (
              <Typography.Text type="secondary">No tickets for this business.</Typography.Text>
            ) : (
              <div style={{ maxHeight: 440, overflowY: 'auto' }}>
                {businessTicketsAllList.map((t: any) => (
                  <div
                    key={t.id}
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                    }}
                  >
                    <Typography.Link
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/workspace/number/${t.ticket_number}`, {
                          state: { openInNewTab: true },
                        });
                      }}
                      style={{ fontSize: 14, fontWeight: 500 }}
                    >
                      #{t.ticket_number}
                    </Typography.Link>
                    <Typography.Text type="secondary" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>
                      {t.subject}
                    </Typography.Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 12 }}>
                      <span>
                        <Typography.Text type="secondary">Status: </Typography.Text>
                        <Tag color={t.status === 'solved' ? 'green' : t.status === 'pending' ? 'orange' : t.status === 'open' ? 'blue' : 'default'} style={{ margin: 0 }}>
                          {t.status}
                        </Tag>
                      </span>
                      <span>
                        <Typography.Text type="secondary">Created: </Typography.Text>
                        <Typography.Text>{dayjs(t.created_at).format('MMM D, YYYY h:mm A')}</Typography.Text>
                      </span>
                      <span>
                        <Typography.Text type="secondary">Assignee: </Typography.Text>
                        <Typography.Text>
                          {t.assigned_to_name && t.assigned_to_last_name
                            ? `${t.assigned_to_name} ${t.assigned_to_last_name}`
                            : t.assigned_to_name || 'Unassigned'}
                        </Typography.Text>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal>

          {/* Business Services - Only show when not creating new ticket */}
          {!isNewTicket && (
          <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
            <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
              Services
            </Typography.Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(Array.isArray(businessServices) ? businessServices : []).map((svc: any) => {
                const type = svc?.service_type || svc;
                const labelMap: Record<string, string> = {
                  retailzpos: 'RetailZPOS',
                  ptechpos: 'PTechPOS',
                  moolah_points: 'Moolah Points',
                  patelhms: 'Patel HMS',
                  credit_card_processing: 'Credit Card Processing',
                };
                const colorMap: Record<string, string> = {
                  retailzpos: 'blue',
                  ptechpos: 'purple',
                  moolah_points: 'green',
                  patelhms: 'magenta',
                  credit_card_processing: 'gold',
                };
                const label = labelMap[type] || (typeof type === 'string' ? type.replace(/_/g, ' ') : String(type));
                const color = (colorMap[type] as any) || 'blue';
                return (
                  <Tag key={type} color={color}>{label}</Tag>
                );
              })}
            </div>
          </div>
          )}

          {/* Equipment - Only show when not creating new ticket */}
          {!isNewTicket && (
          <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
            <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
              Equipment
            </Typography.Title>
            {businessEquipment.length > 0 ? (
              <List
                dataSource={businessEquipment.flatMap((item: any) => {
                  const quantity = item.quantity || 1;
                  return Array.from({ length: quantity }, (_, i) => ({
                    ...item,
                    key: `${item.id}-${i}`
                  }));
                })}
                renderItem={(item: any) => (
                  <List.Item key={item.key} style={{ padding: '4px 0' }}>
                    <div>
                      <Typography.Text style={{ fontSize: '0.85rem', display: 'block' }}>
                        {item.equipment_type?.replace('_', ' ').toUpperCase()} - {item.model}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {item.location ? `Location: ${item.location}` : 'No location set'}
                      </Typography.Text>
                      {item.notes && (
                        <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic', display: 'block' }}>
                          Note: {item.notes}
                        </Typography.Text>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: '0.85rem' }}>
                No equipment assigned
              </Typography.Text>
            )}
          </div>
          )}

          {/* Quick Actions - Only show when not creating new ticket */}
          {!isNewTicket && (
          <div style={{ padding: window.innerWidth >= 600 ? 16 : 12, borderBottom: '1px solid #E7E0EC' }}>
            <Typography.Title level={5} style={{ fontWeight: 600, marginBottom: 16, marginTop: 0 }}>
              Quick Actions
            </Typography.Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <AntButton
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleQuickAction('solve')}
                disabled={ticket?.status === 'solved'}
                size="small"
                block
                style={{ borderRadius: 8 }}
              >
                Solve
              </AntButton>
              {/* Reopen hidden per request */}
              <AntButton
                danger
                icon={<ExclamationCircleOutlined />}
                onClick={() => handleQuickAction('escalate')}
                size="small"
                block
                style={{ borderRadius: 8 }}
              >
                Escalate
              </AntButton>
            </Space>
          </div>
          )}

        </Card>
        )}
      </Flex>

      {/* Escalation Dialog */}
      <Modal
        title="Escalate Ticket"
        open={escalationDialog}
        onCancel={() => setEscalationDialog(false)}
        onOk={handleEscalate}
        okButtonProps={{ disabled: !selectedAgent || !escalationComment.trim() || escalationMutation.isLoading }}
        okText={escalationMutation.isLoading ? 'Escalating...' : 'Escalate Ticket'}
        destroyOnClose
      >
        <div style={{ paddingTop: 8 }}>
          <Typography.Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            Select a higher-level agent to escalate this ticket to:
          </Typography.Text>

          <AntdSelect
            value={selectedAgent || undefined}
            onChange={(value) => setSelectedAgent(String(value))}
            placeholder="Escalate to Agent"
            style={{ width: '100%', marginBottom: 16 }}
            showSearch
            optionFilterProp="children"
          >
            {availableAgents.map((agent: any) => (
              <Option key={agent.id} value={agent.id}>
                {agent.name} - {agent.level_label}
              </Option>
            ))}
          </AntdSelect>

          <div style={{ marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Escalation Comment (Optional)
            </Typography.Text>
            <GrammarCheckedTextArea
              rows={3}
              placeholder="Explain why you're escalating this ticket..."
              value={escalationComment}
              onValueChange={(value) => setEscalationComment(value)}
            />
          </div>
        </div>
      </Modal>

      {/* Solve Dialog */}
      <Modal
        title="Solve Ticket"
        open={solveDialog}
        onCancel={() => setSolveDialog(false)}
        onOk={handleConfirmSolve}
        okText="Solve Ticket"
        okButtonProps={{ 
          disabled: !ticket?.category_id || !ticket?.type,
          title: (!ticket?.category_id || !ticket?.type) ? 'Please select Category and Type before solving' : undefined
        }}
        destroyOnClose
      >
        <div style={{ paddingTop: 8 }}>
          {/* Validation warnings */}
          {(!ticket?.category_id || !ticket?.type) && (
            <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#fffbe6', borderRadius: 4, border: '1px solid #ffe58f' }}>
              <Typography.Text style={{ fontWeight: 'bold', marginBottom: 8, display: 'block', color: '#ad6800' }}>
                ⚠️ Required Fields Missing
              </Typography.Text>
              {!ticket?.category_id && (
                <Typography.Text style={{ display: 'block', color: '#ad6800' }}>
                  • Please select a Category for this ticket
                </Typography.Text>
              )}
              {!ticket?.type && (
                <Typography.Text style={{ display: 'block', color: '#ad6800' }}>
                  • Please select a Type for this ticket
                </Typography.Text>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Typography.Text style={{ minWidth: 120 }}>Comment Type</Typography.Text>
                  <AntdSelect
              value={solveCommentType}
              onChange={(v) => setSolveCommentType(v as 'public' | 'internal')}
              style={{ width: 160 }}
              size="middle"
            >
              <Option value="public">Public</Option>
              <Option value="internal">Internal</Option>
            </AntdSelect>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Comment (optional)
            </Typography.Text>
            <GrammarCheckedTextArea
              rows={3}
              placeholder="Add a closing comment..."
              value={solveComment}
              onValueChange={(value) => setSolveComment(value)}
            />
          </div>
        </div>
      </Modal>

      {/* Add Follower Dialog */}
      <Modal
        title="Add Follower"
        open={addFollowerDialog}
        onCancel={() => setAddFollowerDialog(false)}
        onOk={handleAddFollower}
        okButtonProps={{
          disabled:
            (followerType === 'email' && !followerEmail.trim()) ||
            (followerType === 'user' && !followerUserId)
        }}
        okText="Add Follower"
        destroyOnClose
      >
        <div style={{ paddingTop: 8 }}>
          <AntdSelect
            value={followerType}
            onChange={(value) => setFollowerType(value as 'user' | 'email')}
            style={{ width: '100%', marginBottom: 16 }}
          >
            <Option value="email">Email Address</Option>
            <Option value="user">Agent/User</Option>
          </AntdSelect>

          {followerType === 'email' ? (
            <div style={{ marginBottom: 16 }}>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                Email Address
              </Typography.Text>
              <AntInput
                type="email"
                value={followerEmail}
                onChange={(e) => setFollowerEmail(e.target.value)}
                placeholder="Enter email address"
              />
            </div>
          ) : (
            <AntdSelect
              value={followerUserId || undefined}
              onChange={(value) => setFollowerUserId(String(value))}
              placeholder="Select Agent"
              style={{ width: '100%' }}
              showSearch
              options={assigneeSelectOptionsTicket}
              filterOption={(input, option: any) => {
                const searchText = (input || '').toLowerCase().trim();
                if (!searchText) return true;
                const optValue = option?.value;
                if (optValue != null && String(optValue).toLowerCase() === searchText) return true;
                const searchable = (option?.searchable ?? option?.label ?? '').toLowerCase();
                return searchable.includes(searchText);
              }}
              dropdownStyle={{ minWidth: 300 }}
              dropdownMatchSelectWidth={false}
              optionRender={(optOrPayload: any) => {
                const opt = optOrPayload?.option ?? optOrPayload;
                const val = opt?.value != null ? String(opt.value) : '';
                if (!val) return null;
                const user = getUserForOption(val, assigneeOptions);
                if (!user) return <span>{opt?.label ?? ''}</span>;
                let roleDisplay = user.role_display_name;
                if (!roleDisplay) {
                  const roleName = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace('_', ' ') : 'Agent';
                  roleDisplay = user.agent_level ? `${roleName} (Level ${user.agent_level})` : roleName;
                }
                const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%', overflow: 'hidden', padding: '2px 0' }}>
                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                    <span style={{ fontSize: '12px', color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{roleDisplay}</span>
                  </div>
                );
              }}
            >
            </AntdSelect>
          )}
        </div>
      </Modal>

      {/* Audit Log Modal (Admin only) */}
      <Modal
        title="Ticket Audit Log"
        open={!!user && user.role === 'admin' && auditOpen}
        onCancel={() => setAuditOpen(false)}
        footer={null}
        width={700}
      >
        {auditLoading ? (
          <Spin size="large" style={{ display: 'flex', justifyContent: 'center', padding: 40 }} />
        ) : (auditLogs as any[]).length === 0 ? (
          <Typography.Text type="secondary">No changes logged yet.</Typography.Text>
        ) : (
          <List
            dataSource={(auditLogs as any[]).filter((log: any) => log.ticket_id === ticketIdForApi)}
            renderItem={(log: any) => (
              <List.Item key={log.id} style={{ padding: '4px 0' }}>
                <div>
                  <Typography.Text style={{ fontWeight: 600, display: 'block' }}>
                    <strong>{log.first_name} {log.last_name}</strong> changed <strong>{resolveAuditFieldName(log.field, log.new_value || log.old_value)}</strong>
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    {dayjs(log.created_at).format('MMM D, YYYY h:mm A')} — "{resolveAuditValue(log.field, log.old_value)}" → "{resolveAuditValue(log.field, log.new_value)}"
                  </Typography.Text>
                </div>
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Create Follow-up Ticket Dialog */}
      <Modal
        title="Create Follow-up Ticket"
        open={followUpDialogOpen}
        onCancel={() => {
          setFollowUpDialogOpen(false);
          setFollowUpSubject('');
          setFollowUpDescription('');
        }}
        onOk={handleCreateFollowUp}
        okText="Create Follow-up Ticket"
        okButtonProps={{
          loading: createFollowUpMutation.isLoading,
          disabled: !followUpSubject.trim() || !followUpDescription.trim()
        }}
        destroyOnClose
        width={600}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          <Alert
            message="Follow-up Ticket"
            description={`This will create a new ticket linked to ticket #${ticket?.ticket_number}. The new ticket will include the last comment from this ticket.`}
            type="info"
            showIcon
          />
          
          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Subject <span style={{ color: '#ff4d4f' }}>*</span>
            </Typography.Text>
            <AntInput
              placeholder={`Follow-up: ${ticket?.subject || ''}`}
              value={followUpSubject}
              onChange={(e) => setFollowUpSubject(e.target.value)}
              size="large"
              status={!followUpSubject.trim() ? 'error' : ''}
            />
            {!followUpSubject.trim() && (
              <Typography.Text type="danger" style={{ marginTop: 4, display: 'block', fontSize: 12 }}>
                Subject is required
              </Typography.Text>
            )}
          </div>

          <div>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Additional Description <span style={{ color: '#ff4d4f' }}>*</span>
            </Typography.Text>
            <GrammarCheckedTextArea
              placeholder="Add any additional information for the follow-up ticket..."
              value={followUpDescription}
              onValueChange={(value) => setFollowUpDescription(value)}
              rows={4}
              status={!followUpDescription.trim() ? 'error' : ''}
            />
            {!followUpDescription.trim() && (
              <Typography.Text type="danger" style={{ marginTop: 4, display: 'block', fontSize: 12 }}>
                Additional Description is required
              </Typography.Text>
            )}
          </div>
        </div>
      </Modal>

      {/* Recent Ticket Modal - Shows last comment and open button */}
      <Modal
        title={`Ticket #${selectedTicketForModal?.ticket_number}`}
        open={recentTicketsModalVisible}
        onCancel={() => {
          setRecentTicketsModalVisible(false);
          setSelectedTicketForModal(null);
        }}
        footer={[
          <AntButton key="close" onClick={() => {
            setRecentTicketsModalVisible(false);
            setSelectedTicketForModal(null);
          }}>
            Close
          </AntButton>,
          <AntButton
            key="open"
            type="primary"
            onClick={() => {
              if (selectedTicketForModal?.ticket_number) {
                window.open(`/workspace/number/${selectedTicketForModal.ticket_number}`, '_blank');
              }
            }}
          >
            Open in New Tab
          </AntButton>
        ]}
        width={600}
      >
        {selectedTicketForModal && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Typography.Text strong>Subject: </Typography.Text>
                  <Typography.Text>{selectedTicketForModal.subject}</Typography.Text>
                </div>
                <div>
                  <Typography.Text strong>Status: </Typography.Text>
                  <Tag color={
                    selectedTicketForModal.status === 'solved' ? 'green' :
                    selectedTicketForModal.status === 'pending' ? 'orange' :
                    selectedTicketForModal.status === 'open' ? 'blue' : 'default'
                  }>
                    {selectedTicketForModal.status}
                  </Tag>
                </div>
                <div>
                  <Typography.Text strong>Priority: </Typography.Text>
                  <Tag color={
                    selectedTicketForModal.priority === 'urgent' ? 'red' :
                    selectedTicketForModal.priority === 'high' ? 'orange' :
                    selectedTicketForModal.priority === 'medium' ? 'blue' : 'default'
                  }>
                    {selectedTicketForModal.priority}
                  </Tag>
                </div>
                <div>
                  <Typography.Text strong>Created: </Typography.Text>
                  <Typography.Text type="secondary">
                    {dayjs(selectedTicketForModal.created_at).format('MMM D, YYYY h:mm A')}
                  </Typography.Text>
                </div>
              </Space>
            </Card>

            <Divider>Last Comment</Divider>

            {lastCommentData ? (
              <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography.Text strong>
                      {lastCommentData.user?.first_name} {lastCommentData.user?.last_name}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(lastCommentData.created_at).format('MMM D, YYYY h:mm A')}
                    </Typography.Text>
                  </div>
                  <Tag color={lastCommentData.is_internal ? 'orange' : 'blue'}>
                    {lastCommentData.is_internal ? 'Internal' : 'Public'}
                  </Tag>
                  <div 
                    style={{ 
                      marginTop: 8,
                      padding: 12,
                      backgroundColor: 'white',
                      borderRadius: 4,
                      border: '1px solid #d9d9d9'
                    }}
                    dangerouslySetInnerHTML={{ __html: lastCommentData.comment }}
                  />
                </Space>
              </Card>
            ) : (
              <Typography.Text type="secondary">No comments found for this ticket.</Typography.Text>
            )}
          </div>
        )}
      </Modal>
    </div>
  );};

export default AgentWorkspace;